/**
 * AppDetailPage — one deployed managed-app instance, tabbed surface.
 *
 * Mounted at /projects/:projectId/apps/:instanceId as a child of
 * ProjectLayout. Header chrome (title + status pill + meta + actions)
 * matches PipelineDetailPage so the two read as siblings. Below the
 * header is a tab strip — same `.pipeline-detail-tabs/.pipeline-detail-tab`
 * styling, same `?tab=…` URL convention as PipelineDetailPage, so
 * back/forward + share-the-link both work.
 *
 *   Overview       — horizontal meta row (URL / instance id / running /
 *                    resources / storage / hourly cost) + all the
 *                    config sections below it (bootstrap script, env
 *                    vars table, agent-ops scheduler, optional setup
 *                    hint). Mirrors PipelineDetailPage's `pipeline-stat-grid`.
 *   Logs           — 3s-poll tail of agent-ops logs (unchanged from v1)
 *   Conversations  — "Agent activity": 10s-poll list of structured run
 *                    records from GET /apps/:id/runs (the control-plane
 *                    app-runs table the agent-ops daemon pushes to).
 *                    Each row expands to lazy-fetch the full record
 *                    (prompts + result) via GET /apps/:id/runs/:runId.
 *
 * Default tab when `?tab` is missing or unknown: overview. Legacy
 * `?tab=config` links redirect to `?tab=overview` on mount so any
 * bookmarked / shared Config-tab links from the previous shape don't
 * land on an empty body.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  arrowBackOutline,
  copyOutline,
  openOutline,
  trashOutline,
  pauseOutline,
  playOutline,
  sparklesOutline,
  cloudUploadOutline,
  closeOutline,
  globeOutline,
  createOutline,
} from 'ionicons/icons';
import { IonIcon, IonButton, IonSpinner } from '@ionic/react';
import cronstrue from 'cronstrue';
import { Apps } from '../../services/apps';
import { timeAgo } from '../../utils/timeAgo';
import AppStatus from './AppStatus';
import { resolveTier } from '../../components/billing/AppPricingBadge';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import DataTable from '../../components/shared/DataTable/DataTable';
import Credits, { formatRate, formatBalance } from '../../services/credits';
import InsufficientCreditsModal from '../../components/billing/InsufficientCreditsModal';
import MktAlertModal from '../../components/molecules/MktAlertModal/MktAlertModal';
import DangerButton from '../../components/shared/DangerButton/DangerButton';
import DangerZone from '../../components/shared/DangerZone/DangerZone';
import AgentOpsToggle from '../../components/shared/AgentOpsToggle/AgentOpsToggle';
import ClaudeCredentialModal from '../../components/integrations/ClaudeCredentialModal';
import WorkspaceCredentials from '../../services/workspaceCredentials';
import AppAccessConfig from './AppAccessConfig';
import LogLine, { parseLogLine } from '../../components/shared/LogLine';
import PillButton from '../../components/shared/PillButton';
import MetricChart from '../../components/shared/MetricChart/MetricChart';
// Reuse the pipeline detail stylesheet for the whole shell (header,
// tab strip, tab body, skeletons, stat grid). Apps marketplace
// stylesheet is still imported for legacy helper classes (e.g.
// `.apps-modal-helper`) used by the section bodies below the meta
// row. Local additions live in AppDetailPage.css.
import AgentModelModal from '../../components/molecules/AgentModelModal/AgentModelModal';
import '../PipelinesPage/PipelineDetailPage.css';
import '../AppsMarketplacePage/AppsMarketplacePage.css';
import './AppDetailPage.css';

const TABS = [
  { id: 'overview',      label: 'Overview' },
  { id: 'logs',          label: 'Instance log' },
  { id: 'conversations', label: 'Agent activity' },
  { id: 'settings',      label: 'Settings' },
  { id: 'env',           label: 'ENV' },
];

const AppDetailPage = () => {
  const { projectId, instanceId, slug } = useParams();
  // Solo (dedicated-EC2) apps mount at /apps/solo/:slug and are keyed by
  // appSlug — they're NOT in the cloud instanceId-keyed table, so the whole
  // page takes a separate load path (`getSoloStatus`) and a slimmed-down
  // body. Cloud apps (`:instanceId`) are completely unaffected.
  const isSolo = !!slug;
  if (isSolo) return <SoloAppDetailPage slug={slug} projectId={projectId} />;
  return <CloudAppDetailPage projectId={projectId} instanceId={instanceId} />;
};

// ─── Custom domain (bring-your-own) card ────────────────────────────────
//
// Additive settings-tab card that sits beside the vanity Subdomain card.
// Cloud apps only (solo apps don't share the fleet ALB, and the backend
// 400s them) — the parent only renders this on the cloud path AND gates on
// deployMode, so it never shows for solo.
//
// State machine mirrors the backend `status`:
//   none    → input + Save (POST /domain)
//   pending → show domain + the 2 DNS records to publish; polls every 15s
//             so it flips to active on its own; Remove available.
//   active  → green "Live" badge + link; Remove available.
//   failed  → red "Validation failed" + Try again (re-POST) / Remove.
//
// Reuses the same copy-row chrome (.pipeline-stat-copy + copyOutline), the
// .app-convo-badge--* status pills, .app-logs-ctl--input inputs and
// PillButton the rest of the page uses — no new design system.
const CustomDomainCard = ({ instanceId, onCopy }) => {
  const [domain, setDomain] = useState('');     // current saved domain
  const [status, setStatus] = useState('none'); // none|pending|active|failed
  const [dns, setDns] = useState(null);         // { validation, routing }
  const [input, setInput] = useState('');       // the form field
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState(null);         // inline error (400/409/etc)
  const pollRef = useRef(null);

  // Apply a GET/POST response into local state.
  const applyState = useCallback((res) => {
    setStatus(res?.status || 'none');
    setDomain(res?.domain || '');
    setDns(res?.dns || null);
  }, []);

  // Initial load of the current domain state.
  const refresh = useCallback(async () => {
    try {
      const res = await Apps.getCustomDomain(instanceId);
      applyState(res);
    } catch (_e) {
      // A missing/none domain shouldn't surface as an error — treat any
      // load failure as "no domain yet" so the form still renders.
      setStatus('none');
      setDomain('');
      setDns(null);
    } finally {
      setLoaded(true);
    }
  }, [instanceId, applyState]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll every 15s WHILE pending so the card flips to active/failed on its
  // own once the user's DNS validates. Cleared on unmount or when status
  // leaves 'pending'.
  useEffect(() => {
    if (status !== 'pending') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      try {
        const res = await Apps.getCustomDomain(instanceId);
        applyState(res);
      } catch (_e) { /* transient — keep polling */ }
    }, 15000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [status, instanceId, applyState]);

  const submit = async (value) => {
    const d = (value || '').trim().toLowerCase();
    if (!d || saving) return;
    setErr(null);
    setSaving(true);
    try {
      const res = await Apps.setCustomDomain(instanceId, d);
      applyState(res);
      setInput('');
    } catch (e) {
      // 400 (invalid / solo) and 409 (taken) carry a useful body.error;
      // surface it inline (red) rather than a vanishing toast.
      setErr(e?.body?.error || e?.message || 'Could not set custom domain');
    } finally {
      setSaving(false);
    }
  };

  // Manual "Check now" — reuses the same refresh() the poll/mount use so the
  // card flips to active/failed immediately if the cert/DNS has resolved.
  const checkNow = async () => {
    if (checking) return;
    setChecking(true);
    try {
      await refresh();
    } finally {
      setChecking(false);
    }
  };

  const remove = async () => {
    if (removing) return;
    setErr(null);
    setRemoving(true);
    try {
      await Apps.removeCustomDomain(instanceId);
      setStatus('none');
      setDomain('');
      setDns(null);
      setInput('');
    } catch (e) {
      setErr(e?.body?.error || e?.message || 'Could not remove custom domain');
    } finally {
      setRemoving(false);
    }
  };

  // One copyable DNS record row — mirrors the Overview public-URL copy row.
  const DnsRow = ({ index, label, record }) => {
    if (!record) return null;
    const value = `${record.name} → ${record.value}`;
    return (
      <li className="app-domain-dns-row">
        <span className="app-domain-dns-num" aria-hidden="true">{index}</span>
        <span className="app-domain-dns-type">{record.type || 'CNAME'}</span>
        <code className="app-domain-dns-name" title={record.name}>{record.name}</code>
        <span className="app-domain-dns-arrow" aria-hidden="true">→</span>
        <code className="app-domain-dns-value" title={record.value}>{record.value}</code>
        <button
          type="button"
          className="pipeline-stat-copy"
          onClick={() => onCopy(value, 'DNS record')}
          title="Copy record"
        >
          <IonIcon icon={copyOutline} />
        </button>
        <span className="app-domain-dns-hint">{label}</span>
      </li>
    );
  };

  return (
    <div className="page-card app-detail-card">
      <h3 className="pipeline-section-title">Custom domain</h3>

      {/* none — show the add form */}
      {status === 'none' && (
        <>
          <p className="pipeline-section-note">
            Point a domain you own at this app. Use a subdomain (e.g.{' '}
            <code>app.yourdomain.com</code>) — root domains like{' '}
            <code>yourdomain.com</code> aren’t supported yet.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <input
              type="text"
              className="app-logs-ctl app-logs-ctl--input"
              style={{ flex: '1 1 220px', minWidth: 180 }}
              value={input}
              onChange={(e) => { setInput(e.target.value); if (err) setErr(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(input); }}
              placeholder="app.yourdomain.com"
              autoComplete="off"
              spellCheck="false"
              aria-label="Custom domain"
              disabled={saving || !loaded}
            />
            <PillButton
              onClick={() => submit(input)}
              loading={saving}
              disabled={!input.trim() || !loaded}
              title="Attach this custom domain"
            >
              Save
            </PillButton>
          </div>
        </>
      )}

      {/* pending — show domain + the 2 DNS records to publish */}
      {status === 'pending' && (
        <>
          <div className="app-domain-head">
            <strong className="app-domain-name">{domain}</strong>
            <span className="app-convo-badge app-convo-badge--warn">Pending DNS</span>
          </div>
          <p className="pipeline-section-note">
            Add these 2 records at your DNS provider:
          </p>
          <ul className="app-domain-dns-list">
            <DnsRow index="①" label="verifies ownership" record={dns?.validation} />
            <DnsRow index="②" label="points your domain at the app" record={dns?.routing} />
          </ul>
          <p className="pipeline-section-note">
            Add both, then this turns green automatically — usually a few minutes.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <PillButton onClick={checkNow} loading={checking} title="Re-check DNS + certificate now">
              Check now
            </PillButton>
            <PillButton onClick={remove} loading={removing} title="Detach this custom domain">
              Remove
            </PillButton>
          </div>
        </>
      )}

      {/* active — live link + remove */}
      {status === 'active' && (
        <>
          <div className="app-domain-head">
            <a
              href={`https://${domain}`}
              target="_blank"
              rel="noreferrer noopener"
              className="app-domain-name app-domain-name--link"
            >
              {domain}
            </a>
            <span className="app-convo-badge app-convo-badge--ok">✓ Live</span>
          </div>
          <p className="pipeline-section-note">
            Your app is live at{' '}
            <a href={`https://${domain}`} target="_blank" rel="noreferrer noopener">
              https://{domain}
            </a>.
          </p>
          <PillButton onClick={remove} loading={removing} title="Detach this custom domain">
            Remove
          </PillButton>
        </>
      )}

      {/* failed — retry / remove */}
      {status === 'failed' && (
        <>
          <div className="app-domain-head">
            <strong className="app-domain-name">{domain}</strong>
            <span className="app-convo-badge app-convo-badge--err">Validation failed</span>
          </div>
          <p className="pipeline-section-note">
            We couldn’t validate <strong>{domain}</strong>. Double-check the DNS
            records at your provider, then try again.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <PillButton onClick={() => submit(domain)} loading={saving} title="Re-attempt validation">
              Try again
            </PillButton>
            <PillButton onClick={remove} loading={removing} title="Detach this custom domain">
              Remove
            </PillButton>
          </div>
        </>
      )}

      {err && (
        <p className="app-domain-error" role="alert">{err}</p>
      )}
    </div>
  );
};

const CloudAppDetailPage = ({ projectId, instanceId }) => {
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  // Inline (non-toast) message for the 409 service-missing case — the app's
  // ECS service is gone and Start can't recover it (the user must redeploy).
  const [startError, setStartError] = useState(null);
  // Data export: 'idle' | 'preparing' | 'ready'. While 'preparing' a poll
  // (every 5s) HeadObjects the export key; on 'ready' we auto-trigger the
  // browser download from the short-TTL presigned URL.
  const [exportState, setExportState] = useState('idle');
  const exportPollRef = useRef(null);

  // Settings → Subdomain card. The input holds the LABEL (the part before
  // .apps.zibby.dev), stripped to the DNS charset live (same as the deploy
  // modal). Seeded from the row's current subdomain on load. `savingSubdomain`
  // gates the Save button.
  const [subdomainLabel, setSubdomainLabel] = useState('');
  const [savingSubdomain, setSavingSubdomain] = useState(false);

  // Agent-automation ENABLE gate (BYOK Claude token). Mirrors the deploy
  // modal's cred flow (AppsMarketplaceSection): fetch getClaudeStatus on the
  // ENABLE click → State A (token exists) shows a confirm dialog with the
  // masked suffix; State B (no token) opens ClaudeCredentialModal and enables
  // on save. `agentOpsCred` caches the status ({ hasClaude, mode, suffix });
  // `agentOpsConfirmOpen` drives the confirm dialog; `agentOpsClaudeModalOpen`
  // drives the paste-token modal. DISABLE is ungated (no token needed).
  const [agentOpsCred, setAgentOpsCred] = useState(null);
  const [agentOpsConfirmOpen, setAgentOpsConfirmOpen] = useState(false);
  const [agentOpsClaudeModalOpen, setAgentOpsClaudeModalOpen] = useState(false);

  // Tab state — read from `?tab=…`, fall back to 'overview' on missing
  // / unknown. Mirrors PipelineDetailPage's pattern so users can deep-
  // link any tab.
  const tabFromUrl = searchParams.get('tab');
  const activeTab = TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : 'overview';
  const setActiveTab = (id) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  // Legacy `?tab=config` links → redirect to Overview (Config content
  // got merged into the Overview tab). Replace history so the user
  // can't hit "back" into a dead tab id.
  useEffect(() => {
    if (tabFromUrl === 'config') {
      setSearchParams({ tab: 'overview' }, { replace: true });
    }
  }, [tabFromUrl, setSearchParams]);

  const fetchApp = useCallback(async ({ silent = false } = {}) => {
    // silent = refetch WITHOUT flipping the page into the loading skeleton.
    // Post-action refreshes (agent save, start/stop) must be silent — flipping
    // `loading` re-renders the whole detail page to the skeleton, which reads
    // as a full-page reload (this is the "modal save reloads the page" bug).
    if (!silent) setLoading(true);
    try {
      const data = await Apps.getInstance(instanceId);
      setApp(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch app:', err);
      setError(err);
      setApp(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { fetchApp(); }, [fetchApp]);

  // Seed the Settings → Subdomain input from the row's current subdomain.
  // We strip off the `.apps.zibby.dev` (or whatever the base host is)
  // suffix so the input shows ONLY the editable label. Re-runs when the
  // saved subdomain changes (initial load + post-save refetch).
  const currentSubdomainLabel = (app?.subdomain || '').split('.')[0] || '';
  useEffect(() => {
    setSubdomainLabel(currentSubdomainLabel);
  }, [currentSubdomainLabel]);

  // Save a new vanity subdomain. Backend re-validates (reserved/charset/
  // length → 400) + availability (409) and re-points the ALB host-header
  // rule; on success we refetch so the Overview URL + this card update.
  const handleSaveSubdomain = async () => {
    const label = subdomainLabel.trim();
    if (!label || savingSubdomain) return;
    setSavingSubdomain(true);
    try {
      const res = await Apps.updateSubdomain(app.instanceId, label);
      setToast?.({
        isOpen: true,
        message: `Subdomain updated — ${res?.subdomain || `${label}.apps.zibby.dev`}`,
        color: 'success',
      });
      fetchApp({ silent: true });
    } catch (err) {
      // Surface the backend's 400 (reserved/invalid) / 409 (taken) message.
      setToast?.({
        isOpen: true,
        message: err?.body?.error || err?.message || 'Could not update subdomain',
        color: 'danger',
      });
    } finally {
      setSavingSubdomain(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      setToast?.({ isOpen: true, message: `Copied ${label}`, color: 'success' });
    });
  };

  // In-place agent-ops upgrade — re-registers the per-instance task
  // def with the latest image tag and force-redeploys the service.
  // EFS access point + bridgeToken + ALB wiring all preserved by the
  // backend's `upgradeApp` handler (see backend/src/handlers/apps.js).
  // Modal closes on success and we refetch the app to clear the pill.
  const handleUpdate = async () => {
    if (!app || updating) return;
    setUpdating(true);
    try {
      await Apps.upgrade(app.instanceId, { version: app.latestAgentOpsVersion });
      setToast?.({ isOpen: true, message: 'Upgrade started — task will restart in ~1 min', color: 'success' });
      setUpdateModalOpen(false);
      // Refetch so the pill disappears once the new TD is registered.
      // The actual rolling restart takes longer — but the row's
      // agentOpsVersion flips as soon as the new TD is the active one.
      fetchApp({ silent: true });
    } catch (err) {
      setToast?.({ isOpen: true, message: `Upgrade failed: ${err.message}`, color: 'danger' });
    } finally {
      setUpdating(false);
    }
  };

  // Explicit Start/Restart for a stopped / paused / failed app. Bumps the
  // ECS service back to desiredCount=1 (backend force-new-deploys), flips the
  // row to running, then refetches so the status pill updates. The 409
  // service-missing case (service gone, redeploy required) surfaces inline
  // rather than as a transient toast.
  const handleStart = async () => {
    if (!app || starting) return;
    setStarting(true);
    setStartError(null);
    try {
      const res = await Apps.start(app.instanceId);
      if (res?.started === false && res?.reason === 'already-running') {
        setToast?.({ isOpen: true, message: 'App is already running', color: 'success' });
      } else {
        setToast?.({ isOpen: true, message: 'Start requested — task will come up in ~1 min', color: 'success' });
      }
      fetchApp({ silent: true });
    } catch (err) {
      // service-missing → clear, persistent inline guidance (not a toast).
      if (err?.body?.reason === 'service-missing' || (err?.status === 409 && /service no longer exists/i.test(err?.message || ''))) {
        setStartError('This app’s service no longer exists — redeploy the app to bring it back.');
      } else {
        setToast?.({ isOpen: true, message: `Start failed: ${err.message}`, color: 'danger' });
      }
    } finally {
      setStarting(false);
    }
  };

  // Stop = PAUSE (scale-to-0 cloud / StopInstances solo). Reversible — Start
  // brings it back via the redeploy/resume path. NOT destroy (that's the
  // Danger zone). Refetch after so the status pill + buttons re-resolve.
  const handleStop = async () => {
    if (!app || stopping) return;
    setStopping(true);
    try {
      const res = await Apps.stop(app.instanceId);
      if (res?.stopped === false && res?.reason === 'already-stopped') {
        setToast?.({ isOpen: true, message: 'App is already stopped', color: 'success' });
      } else {
        setToast?.({ isOpen: true, message: 'Stop requested — the app is pausing', color: 'success' });
      }
      fetchApp({ silent: true });
    } catch (err) {
      setToast?.({ isOpen: true, message: `Stop failed: ${err.message}`, color: 'danger' });
    } finally {
      setStopping(false);
    }
  };

  // Kick off a browser download for a presigned URL without navigating away
  // (an <a download> click leaves the SPA intact).
  const triggerBrowserDownload = (url) => {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Poll the export status until it's ready, then auto-download. Cleared on
  // unmount + when the export resolves.
  const pollExport = useCallback(async () => {
    if (!instanceId) return;
    try {
      const res = await Apps.getExport(instanceId);
      if (res?.status === 'ready' && res.url) {
        if (exportPollRef.current) { clearInterval(exportPollRef.current); exportPollRef.current = null; }
        setExportState('ready');
        triggerBrowserDownload(res.url);
        setToast?.({ isOpen: true, message: 'Export ready — your download is starting', color: 'success' });
      }
    } catch (err) {
      // Transient poll error — keep polling; the next tick may succeed.
      // eslint-disable-next-line no-console
      console.warn('[apps] export poll failed:', err?.message);
    }
  }, [instanceId, setToast]);

  // Start a data export, then poll for readiness.
  const handleExport = async () => {
    if (!app || exportState === 'preparing') return;
    setExportState('preparing');
    try {
      await Apps.exportData(app.instanceId);
      setToast?.({ isOpen: true, message: 'Preparing export — this can take a minute', color: 'success' });
      if (exportPollRef.current) clearInterval(exportPollRef.current);
      exportPollRef.current = setInterval(pollExport, 5000);
      // Also poll once promptly so a fast/cached export resolves quickly.
      pollExport();
    } catch (err) {
      setExportState('idle');
      setToast?.({ isOpen: true, message: `Export failed: ${err.message}`, color: 'danger' });
    }
  };

  // Clear the export poll on unmount.
  useEffect(() => () => {
    if (exportPollRef.current) clearInterval(exportPollRef.current);
  }, []);

  // Destroy lives in the Settings tab's Danger zone now (consistency with
  // pipelines). The DangerZone owns the styled confirm modal + busy state;
  // this is its onConfirm. Cloud apps are Fargate — stopping a task is far
  // less destructive than the solo EBS+S3 data delete, so the DangerZone
  // uses the plain styled confirm variant (no type-to-confirm). Re-throws
  // on failure so the modal stays open for a retry.
  const destroyApp = async () => {
    if (!app) return;
    try {
      await Apps.destroy(app.instanceId);
      setToast?.({ isOpen: true, message: 'App destroyed', color: 'success' });
      navigate(`/projects/${projectId}/apps`);
    } catch (err) {
      setToast?.({ isOpen: true, message: `Destroy failed: ${err.message}`, color: 'danger' });
      throw err;
    }
  };

  // Toggle the in-container agent-ops scheduler. The ENABLE path is gated on a
  // BYOK Claude token (see requestEnableAgentOps) and drives state itself; this
  // handler is the DISABLE path (AgentOpsToggle's optimistic flip → re-throw on
  // error so it reverts). The scheduler stops; the app keeps running.
  const handleAgentOpsToggle = async (next) => {
    try {
      await Apps.setAgentOps(instanceId, next);
      setApp((prev) => (prev ? { ...prev, agentOpsEnabled: next } : prev));
      setToast?.({
        isOpen: true,
        message: next ? 'Agent automation on — redeploying to apply' : 'Agent automation paused — redeploying to apply',
        color: 'success',
      });
    } catch (err) {
      setToast?.({ isOpen: true, message: `Couldn't update agent automation: ${err.message}`, color: 'danger' });
      throw err;
    }
  };

  // Real ENABLE — runs AFTER the token gate clears (confirm with an existing
  // token, or a fresh token saved). Persists + updates the row so the switch
  // flips on; on failure we surface a toast and leave the switch OFF (state is
  // never optimistically flipped on the ENABLE path).
  const doEnableAgentOps = async () => {
    try {
      await Apps.setAgentOps(instanceId, true);
      setApp((prev) => (prev ? { ...prev, agentOpsEnabled: true } : prev));
      setToast?.({ isOpen: true, message: 'Agent automation on — redeploying to apply', color: 'success' });
    } catch (err) {
      setToast?.({ isOpen: true, message: `Couldn't update agent automation: ${err.message}`, color: 'danger' });
    }
  };

  // ENABLE gate (wired into AgentOpsToggle's onRequestEnable). The agent-ops
  // scheduler is 100% BYOK — enabling without a Claude token silently no-ops —
  // so we mirror the deploy modal: fetch getClaudeStatus, then either confirm
  // with the masked suffix (token present) or open ClaudeCredentialModal
  // (absent) and enable on save. Cancelling either leaves the switch OFF.
  const requestEnableAgentOps = async () => {
    const status = await WorkspaceCredentials.getClaudeStatus();
    setAgentOpsCred(status || { hasClaude: false });
    if (status?.hasClaude) {
      setAgentOpsConfirmOpen(true); // State A — confirm with suffix
    } else {
      setAgentOpsClaudeModalOpen(true); // State B — paste a token first
    }
  };

  // Loading state — same skeleton shape PipelineDetailPage renders, so
  // the visual continuity holds when the user clicks from the apps
  // list into a row.
  if (loading) {
    return (
      <div className="pipeline-detail-page" aria-busy="true" aria-label="Loading app">
        <div className="pipeline-detail-skel-header">
          <div className="pipeline-detail-skel pipeline-detail-skel--title" />
          <div className="pipeline-detail-skel pipeline-detail-skel--meta" />
        </div>
        <div className="pipeline-detail-skel-body">
          <div className="pipeline-detail-skel pipeline-detail-skel--row" />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '85%' }} />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <NotFoundPage
        code="404"
        title="App not found"
        subtitle="This app instance may have been destroyed, or you don't have access to it."
        primaryAction={{
          label: 'Back to apps',
          to: `/projects/${projectId}/apps`,
          icon: arrowBackOutline,
        }}
      />
    );
  }

  const publicUrl = app.url || (app.subdomain ? `https://${app.subdomain}` : null);

  return (
    <>
      <Helmet>
        <title>{`${app.name || app.appType || instanceId} · Apps · Zibby`}</title>
      </Helmet>

      <div className="pipeline-detail-page">
        {/* Header: title + meta row + actions. Same layout as the
            pipeline detail page so they read as siblings. */}
        <div className="pipeline-detail-header">
          <div className="pipeline-detail-titlerow">
            <h1 className="pipeline-detail-title">
              {app.displayName || app.name || app.appType || instanceId}
            </h1>
            <span className="pipeline-detail-meta-sep">·</span>
            <AppStatus
              status={app.status}
              runningCount={app.runningCount}
              desiredCount={app.desiredCount}
              variant="header"
            />
            <span className="pipeline-detail-meta-sep">·</span>
            <span className="pipeline-detail-version">
              {app.appType}{app.appVersion ? ` v${app.appVersion}` : ''}
            </span>
            {/* agent-ops version is meaningful even when the Update
                pill isn't showing — users want to know what version
                they're on, not just "you're up to date". */}
            {app.agentOpsVersion && (
              <>
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-version" title="agent-ops sidecar version">
                  agent-ops v{app.agentOpsVersion}
                </span>
              </>
            )}
            <span className="pipeline-detail-meta-sep">·</span>
            <span className="pipeline-detail-time">
              Created {app.createdAt ? timeAgo(app.createdAt) : '—'}
            </span>
          </div>
          <div className="pipeline-detail-actions">
            {/* "Update available" pill — mirrors PipelineDetailPage's
                marketplace-update affordance so apps + workflows share
                the same App-Store-style language. Renders only when
                the backend flags the live agent-ops image tag as older
                than LATEST_AGENT_OPS_VERSION. Sits LEFT of the
                primary actions (Fitts' law).

                Only while RUNNING: upgradeApp rolls the live service, which
                a stopped/failed app doesn't have (it 409s). For those, the
                Start/Retry button already redeploys onto the latest agent-ops
                image — so showing "Update" there is redundant + broken. */}
            {app.agentOpsUpdateAvailable && app.status === 'running' && (
              <button
                type="button"
                className="pipeline-detail-update-pill"
                onClick={() => setUpdateModalOpen(true)}
                title={`Update available — ${app.agentOpsVersion || '?'} → ${app.latestAgentOpsVersion || '?'}`}
              >
                <IonIcon icon={sparklesOutline} aria-hidden="true" />
                Update available
                {app.latestAgentOpsVersion && (
                  <span className="pipeline-detail-update-pill-ver">
                    v{app.latestAgentOpsVersion}
                  </span>
                )}
              </button>
            )}
            {publicUrl && (
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="header-docs-link"
              >
                <IonIcon icon={openOutline} />
                <span>Open</span>
              </a>
            )}
            {/* ONE Start/Stop toggle — same PillButton, same position (right of
                Open), label driven by state. Running -> Stop (pause); paused
                (stopped, not destroying) -> Start (resume). During
                provisioning/building/upgrading/destroying neither shows (the
                app is mid-transition; nothing to toggle). Replaces the old
                two-separate-buttons layout (a left-of-Open Stop + a right-of-Open
                Start that looked different and confused the toggle). */}
            {['running', 'ready', 'active'].includes(app.status) ? (
              <PillButton
                onClick={handleStop}
                loading={stopping}
                title="Stop this app — pauses the service (reversible)"
              >
                Stop
              </PillButton>
            ) : (['stopped', 'failed'].includes(app.status) && !app.destroyedAt) ? (
              <PillButton
                onClick={handleStart}
                loading={starting}
                title={app.status === 'failed'
                  ? 'Retry — redeploy this instance (EFS data preserved)'
                  : 'Start this app — brings the service back up'}
              >
                {app.status === 'failed' ? 'Retry' : 'Start'}
              </PillButton>
            ) : null}
            {/* Destroy moved into the Settings tab's Danger zone (parity
                with pipelines). */}
          </div>
        </div>

        {/* Inline guidance for the 409 service-missing case — Start can't
            recover an app whose ECS service is gone; the user must redeploy.
            Reuses the marketplace helper class already imported on this
            page (no new CSS). */}
        {startError && (
          <p className="apps-modal-helper" role="alert">{startError}</p>
        )}

        {updateModalOpen && (
          <div
            className="pipeline-update-overlay"
            onClick={() => !updating && setUpdateModalOpen(false)}
            role="presentation"
          >
            <div
              className="pipeline-update-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="apps-update-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pipeline-update-modal-head">
                <h2 id="apps-update-title" className="pipeline-update-modal-title">
                  Update {app.name || app.appType || app.instanceId}
                </h2>
                <button
                  type="button"
                  className="pipeline-update-modal-close"
                  aria-label="Close"
                  onClick={() => !updating && setUpdateModalOpen(false)}
                  disabled={updating}
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
              <div className="pipeline-update-modal-body">
                <p className="pipeline-update-version-line">
                  <span className="pipeline-update-version-from">
                    v{app.agentOpsVersion || '?'}
                  </span>
                  <span className="pipeline-update-version-arrow">→</span>
                  <span className="pipeline-update-version-to">
                    v{app.latestAgentOpsVersion || '?'}
                  </span>
                </p>
                {/* Apps don't currently surface per-version release
                    notes from the agent-ops repo. Once we wire a
                    GitHub Releases pull-through, drop notes in here. */}
                <p className="pipeline-update-no-notes">
                  Release notes are published in the agent-ops repo.
                </p>
                <p className="pipeline-update-preserved">
                  Your EFS volume, environment variables, and app data
                  are preserved. The container will restart while the
                  new image rolls out (~1 min).
                </p>
              </div>
              <div className="pipeline-update-modal-actions">
                <button
                  type="button"
                  className="pipeline-update-btn pipeline-update-btn--ghost"
                  onClick={() => setUpdateModalOpen(false)}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pipeline-update-btn pipeline-update-btn--primary"
                  onClick={handleUpdate}
                  disabled={updating}
                >
                  <IonIcon icon={cloudUploadOutline} aria-hidden="true" />
                  {updating ? 'Updating…' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab strip — reuse the same classes PipelineDetailPage uses
            so the visual treatment is identical. */}
        <div className="pipeline-detail-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`pipeline-detail-tab ${activeTab === tab.id ? 'pipeline-detail-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className="pipeline-detail-body">
          {activeTab === 'overview' && (
            <OverviewTab
              app={app}
              instanceId={instanceId}
              publicUrl={publicUrl}
              onCopy={copyToClipboard}
              onRefresh={fetchApp}
              setToast={setToast}
            />
          )}
          {activeTab === 'logs' && (
            <LogsTab app={app} instanceId={instanceId} />
          )}
          {activeTab === 'env' && (
            <EnvTab instanceId={instanceId} />
          )}
          {activeTab === 'conversations' && (
            <ConversationsTab instanceId={instanceId} appStatus={app.status} />
          )}
          {activeTab === 'settings' && (
            <div className="pipeline-tab">
              <div className="page-card app-detail-card">
                <h3 className="pipeline-section-title">Subdomain</h3>
                <p className="pipeline-section-note">
                  This app’s public URL is{' '}
                  <strong>{app.subdomain || `${instanceId}.apps.zibby.dev`}</strong>.
                  Change the subdomain label below — the new address takes effect
                  immediately. Leave at least 3 characters; letters, digits and
                  hyphens only.
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <input
                    type="text"
                    className="app-logs-ctl app-logs-ctl--input"
                    style={{ flex: '1 1 200px', minWidth: 160 }}
                    value={subdomainLabel}
                    onChange={(e) => setSubdomainLabel(
                      e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 63),
                    )}
                    placeholder="my-app"
                    autoComplete="off"
                    spellCheck="false"
                    aria-label="Subdomain label"
                    disabled={savingSubdomain}
                  />
                  <span style={{ opacity: 0.6 }}>.apps.zibby.dev</span>
                  <PillButton
                    onClick={handleSaveSubdomain}
                    loading={savingSubdomain}
                    disabled={
                      !subdomainLabel.trim()
                      || subdomainLabel.trim() === currentSubdomainLabel
                    }
                    title="Save the new subdomain"
                  >
                    Save
                  </PillButton>
                </div>
              </div>
              {/* Custom (bring-your-own) domain — cloud apps only. Solo apps
                  don't share the fleet ALB so they can't take a custom domain
                  (and the backend 400s them); hide the card entirely with a
                  short note rather than show a form that can't succeed. */}
              {app.deployMode === 'solo' ? (
                <div className="page-card app-detail-card">
                  <h3 className="pipeline-section-title">Custom domain</h3>
                  <p className="pipeline-section-note">
                    Custom domains are available for cloud apps.
                  </p>
                </div>
              ) : (
                <CustomDomainCard instanceId={instanceId} onCopy={copyToClipboard} />
              )}
              <div className="page-card app-detail-card">
                <h3 className="pipeline-section-title">Export / Download data</h3>
                <p className="pipeline-section-note">
                  Download a compressed archive (.tar.gz) of this app’s persisted
                  data. Reads the storage volume read-only — your app keeps running.
                  Large volumes may take a minute to prepare; the download starts
                  automatically when it’s ready.
                </p>
                <PillButton
                  onClick={handleExport}
                  disabled={exportState === 'preparing'}
                  title="Export and download this app’s data"
                >
                  {exportState === 'preparing' ? 'Preparing…' : 'Export data'}
                </PillButton>
              </div>
              {/* Resources / Change plan — cloud apps only. Solo apps run on
                  a dedicated EC2 (no in-place ECS resize path); the backend
                  resize endpoint is cloud-instanceId keyed, so hide for solo. */}
              {app.deployMode !== 'solo' && (
                <ChangePlanSection
                  app={app}
                  instanceId={instanceId}
                  onRefresh={fetchApp}
                  setToast={setToast}
                />
              )}
              <div className="page-card app-detail-card">
                <AgentOpsToggle
                  separated={false}
                  enabled={app.agentOpsEnabled}
                  onToggle={handleAgentOpsToggle}
                  onRequestEnable={requestEnableAgentOps}
                />
                <DangerZone
                  label={app.displayName || app.name || app.appType || instanceId}
                  hint="This stops the running task and removes the app. This cannot be undone."
                  buttonLabel="Destroy app"
                  confirmLabel="Destroy"
                  busyLabel="Destroying…"
                  onConfirm={destroyApp}
                />
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Agent-automation ENABLE gate — State A: a Claude token exists, so we
          confirm with its masked suffix before enabling. Confirm → real
          enable; Cancel → switch stays OFF. */}
      <MktAlertModal
        open={agentOpsConfirmOpen}
        onClose={() => setAgentOpsConfirmOpen(false)}
        title="Enable agent automation?"
        ariaLabel="Confirm agent automation"
        primaryLabel="Enable"
        onPrimary={() => { setAgentOpsConfirmOpen(false); doEnableAgentOps(); }}
        secondaryLabel="Cancel"
      >
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          Uses your saved Claude{' '}
          {agentOpsCred?.mode === 'oauth' ? 'subscription' : 'API key'}
          {agentOpsCred?.suffix ? <> (····{agentOpsCred.suffix})</> : null}.
          The app <strong style={{ color: '#fff' }}>redeploys (~1 min)</strong> to start the scheduler.
        </p>
      </MktAlertModal>

      {/* State B: no Claude token yet — paste one. Saving IS the intent, so we
          enable straight away on connect (no second confirm). Closing without
          saving leaves the switch OFF. */}
      <ClaudeCredentialModal
        isOpen={agentOpsClaudeModalOpen}
        onClose={() => setAgentOpsClaudeModalOpen(false)}
        defaultKind="oauth"
        onConnected={({ mode, suffix }) => {
          setAgentOpsCred({ hasClaude: true, mode, suffix });
          setAgentOpsClaudeModalOpen(false);
          doEnableAgentOps();
        }}
      />
    </>
  );
};

// ─── Solo (dedicated-EC2) detail page ───────────────────────────────────
//
// Solo apps live on a dedicated EC2, are keyed by `appSlug` (not the cloud
// instanceId), and load from GET /apps/solo/{slug}/status. They don't share
// the fleet ALB/EFS, so the cloud tabs (logs / env / runs — all instanceId-
// keyed endpoints) don't apply. This is a deliberately minimal surface:
// header (title + status) + an Overview meta row (URL / region / framework /
// instance) + the Dedicated Static IP addon control. Reuses the same
// pipeline-detail / pipeline-stat chrome so it reads as a sibling of the
// cloud detail page.
// ─── Solo instance-log section ─────────────────────────────────────────
// LIVE journald tail for a dedicated-EC2 (solo) app. The backend reads
// `agent-opsd.service` over SSM and returns the SAME `lines: string[]`
// shape the cloud LogsTab consumes, so we reuse the LogLine atom +
// `.app-logs-*` body classes. No load-older pagination (journald returns a
// bounded tail) — just a live poll + filter box. When the instance isn't
// running the backend returns `{ live:false, note }`, rendered as a muted
// inline `.apps-modal-helper` message.
const SoloLogsSection = ({ slug, isRunning }) => {
  const [logs, setLogs] = useState([]);
  const [note, setNote] = useState(null);
  // When the instance is stopped the backend returns stored CloudWatch
  // history (live:false + lines). We mark it as History so the UI doesn't
  // imply a live tail. `source==='cloudwatch'` is the explicit history signal.
  const [isHistory, setIsHistory] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [filter, setFilter] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [paused, setPaused] = useState(false);
  const logsBodyRef = useRef(null);
  const stickyBottomRef = useRef(true);

  // Debounce the filter box (mirror the cloud LogsTab cadence).
  useEffect(() => {
    const id = setTimeout(() => setAppliedFilter(filter.trim()), 350);
    return () => clearTimeout(id);
  }, [filter]);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    const fetchLatest = async () => {
      const node = logsBodyRef.current;
      if (node) {
        const slack = 24;
        stickyBottomRef.current = (node.scrollHeight - node.scrollTop - node.clientHeight) <= slack;
      }
      try {
        const data = await Apps.getSoloLogs(slug, {
          lines: 200,
          filter: appliedFilter || undefined,
        });
        if (cancelled) return;
        const lines = Array.isArray(data?.lines) ? data.lines : [];
        setLogs(lines);
        // Stopped-VM CloudWatch history: live:false WITH lines. Render them
        // (no note) but flag History so the marker shows "not live".
        const history = data && data.live === false && lines.length > 0;
        setIsHistory(!!history);
        // Only surface the note when there are no lines to show (e.g.
        // no-history-yet / instance-not-running). With history lines present
        // the History marker carries the "not live" meaning instead.
        setNote(data && data.live === false && lines.length === 0
          ? (data.note || 'Live logs are unavailable.')
          : null);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch logs');
      } finally {
        if (!cancelled) setHasLoaded(true);
      }
    };
    fetchLatest();
    // Live poll (3s) only while running + not paused. When stopped we still
    // poll, but slowly (30s) — history barely changes, so don't hammer it.
    if (paused) return () => { cancelled = true; };
    const id = setInterval(fetchLatest, isRunning ? 3000 : 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [slug, isRunning, paused, appliedFilter]);

  // Sticky auto-scroll to bottom on update (unless the user scrolled up).
  useEffect(() => {
    if (!stickyBottomRef.current) return;
    const node = logsBodyRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [logs]);

  const gutterWidth = Math.max(3, String(logs.length).length);
  const isPollingLive = isRunning && !paused && !note && !isHistory;

  return (
    <div className="pipeline-section">
      <div className="pipeline-section-title">Instance log</div>
      <div className="card-header app-logs-header">
        <input
          type="text"
          className="app-logs-ctl app-logs-ctl--input"
          placeholder="Filter…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter logs"
        />
        <div className="app-logs-controls">
          {isPollingLive && (
            <span className="app-logs-live" title="Polling every 3s">Live</span>
          )}
          {isHistory && (
            <span
              className="app-logs-live app-logs-history"
              title="Instance stopped — showing stored logs (not live)"
            >
              History · not live (instance stopped)
            </span>
          )}
          {isRunning && (
            <button
              type="button"
              className="app-logs-ctl app-logs-ctl--btn"
              onClick={() => setPaused((p) => !p)}
              aria-pressed={paused}
            >
              <IonIcon icon={paused ? playOutline : pauseOutline} />
              <span>{paused ? 'Start' : 'Stop'}</span>
            </button>
          )}
        </div>
      </div>

      {note && <p className="apps-modal-helper">{note}</p>}

      <pre ref={logsBodyRef} className="app-logs-body">
        {!hasLoaded ? (
          <span className="app-logs-empty">Loading…</span>
        ) : error ? (
          <span className="app-logs-error">⚠ {error}</span>
        ) : logs.length === 0 ? (
          <span className="app-logs-empty">
            {note
              ? null
              : appliedFilter
                ? `No lines match "${appliedFilter}".`
                : 'No log events found.'}
          </span>
        ) : (
          logs.map((line, i) => (
            <LogLine key={i} lineNo={i + 1} gutterWidth={gutterWidth} {...parseLogLine(line)} />
          ))
        )}
      </pre>
    </div>
  );
};

const SoloAppDetailPage = ({ slug, projectId }) => {
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Agent-automation ENABLE gate (BYOK Claude token) — same flow as the cloud
  // page. See requestEnableAgentOps below.
  const [agentOpsCred, setAgentOpsCred] = useState(null);
  const [agentOpsConfirmOpen, setAgentOpsConfirmOpen] = useState(false);
  const [agentOpsClaudeModalOpen, setAgentOpsClaudeModalOpen] = useState(false);

  const fetchApp = useCallback(async () => {
    setLoading(true);
    try {
      const data = await Apps.getSoloStatus(slug);
      // Shape into the object the header + StaticIpSection expect. A missing
      // row comes back as { appSlug, phase:'unknown' } with no status — treat
      // that as not-found so we render the 404 rather than an empty shell.
      if (!data || (!data.status && data.phase === 'unknown' && !data.instanceId)) {
        setError(new Error('not found'));
        setApp(null);
      } else {
        setApp({
          ...data,
          deployMode: 'solo',
          appSlug: data.appSlug || slug,
          // Solo status surfaces the finer-grained provisioning `phase`
          // while active; fall back to the lifecycle `status` otherwise —
          // same precedence AppsPage's pill uses.
          status: data.status === 'active' ? (data.phase || data.status) : (data.status || data.phase),
        });
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch solo app:', err);
      setError(err);
      setApp(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchApp(); }, [fetchApp]);

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      setToast?.({ isOpen: true, message: `Copied ${label}`, color: 'success' });
    });
  };

  // Destroy lives in the Danger zone section at the bottom of the page
  // (parity with the pipeline + cloud-app Settings danger zones). The
  // DangerZone owns the type-to-confirm modal + busy state; this is its
  // onConfirm. Re-throws on failure so the modal stays open for a retry.
  const destroyApp = async () => {
    if (!app) return;
    try {
      await Apps.destroySolo(app.appSlug);
      setToast?.({ isOpen: true, message: 'App destroyed — VM, EBS volume + S3 backup deleted', color: 'success' });
      navigate(`/projects/${projectId}/apps`);
    } catch (err) {
      setToast?.({ isOpen: true, message: `Destroy failed: ${err.message}`, color: 'danger' });
      throw err;
    }
  };

  // Toggle the agent-ops scheduler on the dedicated VM (persist + SSM restart
  // server-side). The ENABLE path is BYOK-gated (see requestEnableAgentOps);
  // this handler is the DISABLE path (AgentOpsToggle's optimistic flip →
  // re-throw on error so it reverts).
  const handleAgentOpsToggle = async (next) => {
    try {
      await Apps.setSoloAgentOps(app.appSlug, next);
      setApp((prev) => (prev ? { ...prev, agentOpsEnabled: next } : prev));
      setToast?.({
        isOpen: true,
        message: next ? 'Agent automation on' : 'Agent automation paused',
        color: 'success',
      });
    } catch (err) {
      setToast?.({ isOpen: true, message: `Couldn't update agent automation: ${err.message}`, color: 'danger' });
      throw err;
    }
  };

  // Real ENABLE — runs after the token gate clears. On failure: toast + leave
  // the switch OFF (ENABLE never flips optimistically).
  const doEnableAgentOps = async () => {
    try {
      await Apps.setSoloAgentOps(app.appSlug, true);
      setApp((prev) => (prev ? { ...prev, agentOpsEnabled: true } : prev));
      setToast?.({ isOpen: true, message: 'Agent automation on', color: 'success' });
    } catch (err) {
      setToast?.({ isOpen: true, message: `Couldn't update agent automation: ${err.message}`, color: 'danger' });
    }
  };

  // ENABLE gate (wired into AgentOpsToggle's onRequestEnable). The scheduler is
  // 100% BYOK, so we mirror the deploy modal: getClaudeStatus → confirm with
  // the masked suffix (token present) or open ClaudeCredentialModal (absent),
  // then enable. Cancelling either leaves the switch OFF.
  const requestEnableAgentOps = async () => {
    const status = await WorkspaceCredentials.getClaudeStatus();
    setAgentOpsCred(status || { hasClaude: false });
    if (status?.hasClaude) {
      setAgentOpsConfirmOpen(true);
    } else {
      setAgentOpsClaudeModalOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="pipeline-detail-page" aria-busy="true" aria-label="Loading app">
        <div className="pipeline-detail-skel-header">
          <div className="pipeline-detail-skel pipeline-detail-skel--title" />
          <div className="pipeline-detail-skel pipeline-detail-skel--meta" />
        </div>
        <div className="pipeline-detail-skel-body">
          <div className="pipeline-detail-skel pipeline-detail-skel--row" />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '85%' }} />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <NotFoundPage
        code="404"
        title="App not found"
        subtitle="This dedicated app may have been destroyed, or you don't have access to it."
        primaryAction={{
          label: 'Back to apps',
          to: `/projects/${projectId}/apps`,
          icon: arrowBackOutline,
        }}
      />
    );
  }

  // Solo status carries only the canonical `domain` (<slug>.apps.zibby.dev),
  // not the live publicIp — derive the URL from domain (https; TLS is
  // terminated on the VM by Caddy).
  const publicUrl = app.domain ? `https://${app.domain}` : null;
  const publicUrlNoScheme = publicUrl ? publicUrl.replace(/^https?:\/\//, '') : null;

  return (
    <>
      <Helmet>
        <title>{`${app.appSlug} · Apps · Zibby`}</title>
      </Helmet>

      <div className="pipeline-detail-page">
        <div className="pipeline-detail-header">
          <div className="pipeline-detail-titlerow">
            <h1 className="pipeline-detail-title">{app.appSlug}</h1>
            <span className="pipeline-detail-meta-sep">·</span>
            <AppStatus status={app.status} variant="header" />
            <span className="pipeline-detail-meta-sep">·</span>
            <span className="apps-deploy-badge" data-mode="solo">Dedicated</span>
            {app.tier && (
              <>
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-version">{app.tier}</span>
              </>
            )}
            {app.updatedAt && (
              <>
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-time">Updated {timeAgo(app.updatedAt)}</span>
              </>
            )}
          </div>
          <div className="pipeline-detail-actions">
            {publicUrl && (
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="header-docs-link"
              >
                <IonIcon icon={openOutline} />
                <span>Open</span>
              </a>
            )}
            {/* Destroy moved into the Danger zone section below (parity
                with pipelines + cloud apps). */}
          </div>
        </div>

        <div className="pipeline-detail-body">
          <div className="pipeline-tab">
            <div className="pipeline-stat-grid">
              {publicUrl && (
                <div className="pipeline-stat">
                  <div className="pipeline-stat-label">Public URL</div>
                  <div className="pipeline-stat-value">
                    <a
                      href={publicUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="pipeline-stat-value-text"
                    >
                      {publicUrlNoScheme}
                    </a>
                    <button
                      type="button"
                      className="pipeline-stat-copy"
                      onClick={() => copyToClipboard(publicUrl, 'public URL')}
                      title="Copy URL"
                    >
                      <IonIcon icon={copyOutline} />
                    </button>
                  </div>
                </div>
              )}

              <div className="pipeline-stat">
                <div className="pipeline-stat-label">Status</div>
                <div className="pipeline-stat-value">
                  <span className="pipeline-stat-value-text">{app.status || '—'}</span>
                </div>
                {app.detail && <div className="app-overview-substat">{app.detail}</div>}
              </div>

              <div className="pipeline-stat">
                <div className="pipeline-stat-label">Region</div>
                <div className="pipeline-stat-value">
                  <span className="pipeline-stat-value-text">{app.region || '—'}</span>
                </div>
                {app.framework && (
                  <div className="app-overview-substat">{app.framework}</div>
                )}
              </div>

              <div className="pipeline-stat">
                <div className="pipeline-stat-label">Instance</div>
                <div className="pipeline-stat-value">
                  <span className="pipeline-stat-value-text">{app.instanceId || '—'}</span>
                  {app.instanceId && (
                    <button
                      type="button"
                      className="pipeline-stat-copy"
                      onClick={() => copyToClipboard(app.instanceId, 'instance id')}
                      title="Copy ID"
                    >
                      <IonIcon icon={copyOutline} />
                    </button>
                  )}
                </div>
                {app.instanceType && (
                  <div className="app-overview-substat">{app.instanceType}</div>
                )}
              </div>

              {typeof app.monthlyUSD === 'number' && (
                <div className="pipeline-stat">
                  <div className="pipeline-stat-label">Monthly cost</div>
                  <div className="pipeline-stat-value">
                    <span className="pipeline-stat-value-text">${app.monthlyUSD}/mo</span>
                  </div>
                </div>
              )}
            </div>

            {/* Dedicated Static IP addon — solo apps only. Same control the
                cloud Overview tab gates on deployMode==='solo' && appSlug. */}
            {app.appSlug && <StaticIpSection app={app} />}

            {/* Live instance log — journald tail of agent-opsd over SSM.
                `isRunning` is a polling hint only; the backend authoritatively
                returns live:false + a note when the VM is unreachable. The
                displayed `app.status` is the fine-grained phase while active,
                so a running box is "has an instanceId and is not in a
                paused/stopped/terminated state". */}
            <SoloLogsSection
              slug={app.appSlug}
              isRunning={!!app.instanceId
                && !['paused', 'stopped', 'terminated', 'terminating'].includes(app.status)}
            />

            {/* Agent automation on/off — same shared toggle the cloud
                Settings tab uses. Hidden on solo until a persistent agent-ops
                daemon actually runs there (`agentOpsActive`): solo currently
                runs agent-ops as a one-shot installer with no scheduler to
                gate, so a toggle would do nothing. Auto-appears once the
                unified solo daemon ships (status flips agentOpsActive→true). */}
            {app.agentOpsActive !== false && (
              <AgentOpsToggle
                enabled={app.agentOpsEnabled}
                onToggle={handleAgentOpsToggle}
                onRequestEnable={requestEnableAgentOps}
              />
            )}

            {/* Danger zone — type-to-confirm delete (data loss: VM + EBS +
                S3). Same shared component the pipeline + cloud-app settings
                use. */}
            <DangerZone
              label={app.appSlug}
              requireType
              heading="Danger zone"
              hint="Deleting terminates the VM and deletes ALL data — the EBS volume and the S3 (Litestream) backup. This cannot be undone."
              buttonLabel="Delete app"
              confirmLabel="Permanently delete"
              busyLabel="Deleting…"
              onConfirm={destroyApp}
            />
          </div>
        </div>
      </div>

      {/* Agent-automation ENABLE gate — State A: confirm with the masked
          suffix of the existing Claude token. Confirm → enable; Cancel →
          switch stays OFF. */}
      <MktAlertModal
        open={agentOpsConfirmOpen}
        onClose={() => setAgentOpsConfirmOpen(false)}
        title="Enable agent automation?"
        ariaLabel="Confirm agent automation"
        primaryLabel="Enable"
        onPrimary={() => { setAgentOpsConfirmOpen(false); doEnableAgentOps(); }}
        secondaryLabel="Cancel"
      >
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          Uses your saved Claude{' '}
          {agentOpsCred?.mode === 'oauth' ? 'subscription' : 'API key'}
          {agentOpsCred?.suffix ? <> (····{agentOpsCred.suffix})</> : null}.
          The scheduler starts on the dedicated VM.
        </p>
      </MktAlertModal>

      {/* State B: no Claude token yet — paste one. Saving IS the intent, so we
          enable straight away on connect. Closing without saving leaves the
          switch OFF. */}
      <ClaudeCredentialModal
        isOpen={agentOpsClaudeModalOpen}
        onClose={() => setAgentOpsClaudeModalOpen(false)}
        defaultKind="oauth"
        onConnected={({ mode, suffix }) => {
          setAgentOpsCred({ hasClaude: true, mode, suffix });
          setAgentOpsClaudeModalOpen(false);
          doEnableAgentOps();
        }}
      />
    </>
  );
};

// ─── Overview tab ──────────────────────────────────────────────────────
// Horizontal meta row (URL / instance / running / resources / storage /
// hourly cost) + the merged-in Config sections below it. Reuses the
// `pipeline-stat-grid` + `pipeline-stat` classes from PipelineDetailPage
// so the two detail pages look like siblings. Per-section bodies below
// the meta row reuse `pipeline-section` / `pipeline-section-title`.

const OverviewTab = ({ app, instanceId, publicUrl, onCopy, onRefresh, setToast }) => {
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [storage, setStorage] = useState(null);
  const [storageLoading, setStorageLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Goal-based deploys (free-form natural-language install, not a catalog
    // entry) have no appType. Skip the catalog fetch — it would 4xx with
    // "appType is required" and surface a misleading warning to the user
    // who never asked for a catalog app.
    if (!app.appType) { setCatalogLoading(false); return undefined; }
    (async () => {
      try {
        const data = await Apps.getCatalogEntry(app.appType);
        if (!cancelled) setCatalog(data);
      } catch (err) {
        // A MISSING catalog entry is not an error — the app was deployed as a
        // custom / goal-mode app, or its entry was delisted (e.g. n8n, pulled
        // for license reasons). Leave catalog null (RESOURCES renders "—") and
        // show nothing. Only surface genuine fetch failures (5xx / network).
        const status = err?.status ?? err?.response?.status;
        const isMissing = status === 404 || /unknown\s*app\s*type/i.test(err?.message || '');
        if (!cancelled && !isMissing) setCatalogError(err.message || 'Failed to load catalog');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [app.appType]);

  useEffect(() => {
    let cancelled = false;
    const fetchStorage = async () => {
      try {
        const data = await Apps.getStorage(instanceId);
        if (!cancelled) setStorage(data);
      } catch (err) {
        // Storage is non-fatal — render `—` and move on.
        if (!cancelled) setStorage({ usedBytes: null });
      } finally {
        if (!cancelled) setStorageLoading(false);
      }
    };
    // Fetch ONCE on open — storage usage barely moves, and a 60s poll just
    // spammed the network tab (and CORS-errored while the route was missing).
    // No interval.
    fetchStorage();
    return () => { cancelled = true; };
  }, [instanceId]);

  // Only poll metrics while the app is actually running/healthy — a
  // stopped/destroyed/upgrading app has no live container to sample, so
  // polling it just burns calls and 4xxs. `running`/`ready`/`active` are
  // the live phases (mirrors the Start button + isRunning checks elsewhere).
  const metricsLive = ['running', 'ready', 'active'].includes(app.status);

  useEffect(() => {
    let cancelled = false;
    // `silent` refetches (polls) keep the existing charts on-screen and just
    // swap the data underneath; only the very first load flips the spinner.
    const fetchMetrics = async (silent) => {
      try {
        const data = await Apps.getMetrics(instanceId);
        if (!cancelled) setMetrics(data);
      } catch (err) {
        // Metrics are non-fatal — render "No data yet" and move on. Fresh
        // deploys (no CloudWatch samples yet) and the brief window before the
        // route exists both land here. On a silent poll we DON'T clear the
        // charts — a transient blip shouldn't wipe a working dashboard.
        if (!cancelled && !silent) setMetrics(null);
      } finally {
        if (!cancelled && !silent) setMetricsLoading(false);
      }
    };

    // First load always runs (so a freshly-stopped app still shows its last
    // samples / the spinner resolves). The repeating poll is gated on the app
    // being live.
    fetchMetrics(false);

    let id;
    if (metricsLive) {
      // ~20s cadence: one cheap GET per tick, frequent enough to feel live
      // without hammering the metrics route.
      id = setInterval(() => fetchMetrics(true), 20000);
    }
    return () => {
      cancelled = true;
      if (id) clearInterval(id);
    };
  }, [instanceId, metricsLive]);

  // Resources come from the catalog (the immutable shape the app was
  // deployed against); fall back to whatever the row carries so we
  // still render something while the catalog is loading or if it's
  // gone missing.
  const cpu = catalog?.resources?.cpu ?? app.resources?.cpu;
  const memory = catalog?.resources?.memory ?? app.resources?.memory;
  const cpuVcpu = cpu ? (cpu / 1024).toFixed(cpu % 1024 === 0 ? 0 : 2) : null;
  const memGb = memory ? (memory / 1024).toFixed(memory % 1024 === 0 ? 0 : 2) : null;
  const resourcesText = (cpuVcpu || memGb)
    ? `${cpuVcpu ? `${cpuVcpu} vCPU` : ''}${cpuVcpu && memGb ? ' · ' : ''}${memGb ? `${memGb} GB` : ''}`
    : '—';

  // Prefer the catalog's resolved `pricing` object (carries the live backend
  // rate); fall back to the app row's pricing, then a bare tier string.
  const tierInfo = resolveTier(catalog?.pricing || app.pricing || (catalog?.pricing?.tier || app.pricing?.tier || 'standard'));

  // agent-ops hard-codes two scheduler entries on first-run bootstrap
  // (visible in agent-ops logs as `hourly_health_check` and
  // `weekly_upgrade`). They don't come from the catalog — they're
  // baked into agent-ops itself — so the strings are hardcoded here.
  // If/when agent-ops exposes a list-tasks MCP tool the right thing
  // to do is query it; for now this is correct because agent-ops
  // always wires these two.
  const schedulerCrons = [
    { id: 'hourly_health_check', cron: '0 * * * *',  description: 'Verify the app is responding; restart on failure.' },
    { id: 'weekly_upgrade',      cron: '0 9 * * 1',  description: 'Pull the latest published catalog version and redeploy.' },
  ];

  const publicUrlNoScheme = publicUrl ? publicUrl.replace(/^https?:\/\//, '') : null;

  return (
    <div className="pipeline-tab">
      {/* Meta row — same flex-wrap stat grid PipelineDetailPage uses.
          Each `.pipeline-stat` is a column (label on top in small caps
          grey, value below). The grid wraps on narrow viewports via
          `flex-wrap: wrap` on `.pipeline-stat-grid`. */}
      <div className="pipeline-stat-grid">
        {publicUrl && (
          <div className="pipeline-stat">
            <div className="pipeline-stat-label">Public URL</div>
            <div className="pipeline-stat-value">
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="pipeline-stat-value-text"
              >
                {publicUrlNoScheme}
              </a>
              <button
                type="button"
                className="pipeline-stat-copy"
                onClick={() => onCopy(publicUrl, 'public URL')}
                title="Copy URL"
              >
                <IonIcon icon={copyOutline} />
              </button>
            </div>
          </div>
        )}

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Instance ID</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">{app.instanceId}</span>
            <button
              type="button"
              className="pipeline-stat-copy"
              onClick={() => onCopy(app.instanceId, 'instance id')}
              title="Copy ID"
            >
              <IonIcon icon={copyOutline} />
            </button>
          </div>
          {/* CPU architecture under the instance id — small ALL-CAPS
              substat so it reads as a metadata tag, not a stat value.
              Hidden when the field is missing (older rows pre-rollout). */}
          {app.architecture && (
            <div className="app-overview-substat">
              {String(app.architecture).toUpperCase().replace('_', '-')}
            </div>
          )}
        </div>

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Running</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">
              {(app.runningCount ?? 1)} / {(app.desiredCount ?? 1)}
            </span>
          </div>
          <AppStatus
            status={app.status}
            runningCount={app.runningCount}
            desiredCount={app.desiredCount}
          />
        </div>

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Resources</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">{resourcesText}</span>
          </div>
        </div>

        {/* Storage — actual EFS usage sourced from agent-ops's
            `efs_usage` slog lines. AWS doesn't publish per-access-point
            usage as a metric, so the daemon `du`-walks $StateDir every
            60s and emits a JSON log row; backend filters CloudWatch for
            the newest line. Null while we wait for the first sample
            (fresh deploy / agent-ops <0.1.18). */}
        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Storage</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">
              {storageLoading
                ? '…'
                : (storage && typeof storage.usedBytes === 'number')
                  ? formatBytes(storage.usedBytes)
                  : '—'}
            </span>
          </div>
          <div className="app-overview-substat">
            {storage && storage.sampleAt
              ? `EFS · ${timeAgo(storage.sampleAt)}`
              : 'EFS · auto-scaling'}
          </div>
        </div>

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Hourly cost</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">{tierInfo.hourly}</span>
          </div>
          <div className="app-overview-substat">{tierInfo.label}</div>
        </div>

        {/* Agent — this app's AI provider + model. Clicking "Edit" opens a
            modal to change provider/model/token; saving snapshots the
            change on the row + force-redeploys the instance (EFS preserved).
            Cloud (ECS) apps only — solo apps run on a dedicated EC2 with no
            in-place reclone path, so the card hides for them. */}
        {app.deployMode !== 'solo' && (
          <div className="pipeline-stat">
            <div className="pipeline-stat-label">Agent</div>
            <div className="pipeline-stat-value">
              {/* Whole name is clickable too (not just the pencil) — opens the
                  same editor. */}
              <button
                type="button"
                className="app-agent-name-btn"
                onClick={() => setAgentModalOpen(true)}
                title="Edit agent &amp; model"
              >
                <span className="pipeline-stat-value-text">
                  {(app.provider === 'none' || app.noAgent)
                    ? 'No agent'
                    : (app.provider === 'codex' ? 'Codex' : 'Claude')}
                </span>
                <IonIcon icon={createOutline} />
              </button>
            </div>
            <div className="app-overview-substat">
              {(app.provider === 'none' || app.noAgent)
                ? 'Add an AI agent'
                : (app.model ? app.model : 'Default model')}
            </div>
          </div>
        )}
      </div>

      {/* Usage — live time-series from GET /apps/:id/metrics, rendered as a
          2-column grid of compact filled area charts (DigitalOcean/Grafana
          style): CPU%, Memory%, Requests (count/period) and Response Time (ms)
          over the last ~60 min. Each card header shows the latest value. A
          freshly-started / idle app has no samples yet, so empty series render
          a "Collecting data…" overlay rather than breaking. */}
      <div className="page-card app-detail-card">
        <div className="card-header app-usage-header">
          <h2>Usage</h2>
          {/* "Live" pill — only while we're actively polling a running app.
              The dot pulses (CSS keyframe) to signal real-time updates. */}
          {metricsLive && (
            <span className="app-usage-live" title="Updating live">
              <span className="app-usage-live-dot" />
              Live
            </span>
          )}
        </div>
        {metricsLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
            <IonSpinner name="dots" />
            <span className="apps-modal-helper" style={{ margin: 0 }}>Loading…</span>
          </div>
        ) : (
          <div className="app-usage-grid">
            {/* CPU — single green series, fixed 0-100% axis. */}
            <div className="app-usage-chart">
              <div className="app-usage-chart-head">
                <span className="app-usage-chart-title">CPU Usage</span>
                <span className="app-usage-chart-meta">
                  <span className="app-usage-chart-value">
                    {typeof metrics?.cpu?.current === 'number'
                      ? `${Math.round(metrics.cpu.current)}%`
                      : '—'}
                  </span>
                </span>
              </div>
              <MetricChart
                series={[{ label: 'CPU', color: '#34d399', points: metrics?.cpu?.series }]}
                yMax={100}
                yFmt={fmtPct}
                live={metricsLive}
              />
            </div>

            {/* Memory — single teal series, fixed 0-100% axis. */}
            <div className="app-usage-chart">
              <div className="app-usage-chart-head">
                <span className="app-usage-chart-title">Memory</span>
                <span className="app-usage-chart-meta">
                  <span className="app-usage-chart-value">
                    {typeof metrics?.memory?.current === 'number'
                      ? `${Math.round(metrics.memory.current)}%`
                      : '—'}
                  </span>
                </span>
              </div>
              <MetricChart
                series={[{ label: 'Memory', color: '#38bdf8', points: metrics?.memory?.series }]}
                yMax={100}
                yFmt={fmtPct}
                live={metricsLive}
              />
            </div>

            {/* Requests — single amber series, count per 5-min period. */}
            <div className="app-usage-chart">
              <div className="app-usage-chart-head">
                <span className="app-usage-chart-title">Requests</span>
                <span className="app-usage-chart-meta">
                  <span className="app-usage-chart-value">
                    {fmtCount(latestOf(metrics?.requests?.series))}
                  </span>
                </span>
              </div>
              <MetricChart
                series={[{ label: 'Requests', color: '#f59e0b', points: metrics?.requests?.series }]}
                yFmt={fmtCount}
                live={metricsLive}
              />
            </div>

            {/* Response Time — single purple series, latency in ms. */}
            <div className="app-usage-chart">
              <div className="app-usage-chart-head">
                <span className="app-usage-chart-title">Response Time</span>
                <span className="app-usage-chart-meta">
                  <span className="app-usage-chart-value">
                    {fmtMs(latestOf(metrics?.latency?.series))}
                  </span>
                </span>
              </div>
              <MetricChart
                series={[{ label: 'Latency', color: '#a78bfa', points: metrics?.latency?.series }]}
                yFmt={fmtMs}
                live={metricsLive}
              />
            </div>
          </div>
        )}
      </div>

      {/* Catalog error banner — non-fatal; the meta row above still
          renders from `app.*`, but the sections below depend on the
          catalog. Keep the user informed instead of silently empty. */}
      {catalogError && (
        <p className="apps-modal-helper app-logs-error">⚠ {catalogError}</p>
      )}

      {/* Bootstrap script + Env vars + Setup hint removed from end-user
          view — these are catalog internals (install commands, default
          host/port env, prose explaining n8n's onboarding). Users can't
          change them and don't need to see them. Available via CLI:
          `zbx app templates` shows the catalog entry verbatim. */}

      {/* agent-ops scheduler */}
      <div className="page-card app-detail-card">
        <div className="card-header"><h2>agent-ops scheduler</h2></div>
        <p className="apps-modal-helper">
          Every Zibby-managed app runs an in-container scheduler. These two
          jobs are wired automatically; they cannot be edited from the
          dashboard yet.
        </p>
        <table className="app-config-envs">
          <thead>
            <tr><th>Job</th><th>Cron</th><th>Translated</th></tr>
          </thead>
          <tbody>
            {schedulerCrons.map((c) => (
              <tr key={c.id}>
                <td><code className="app-detail-mono">{c.id}</code></td>
                <td><code className="app-detail-mono">{c.cron}</code></td>
                <td>
                  {safeCronstrue(c.cron)}
                  <div className="app-config-cron-hint">{c.description}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AUTHENTICATION — gate the public URL. Sits at the bottom of Overview
          to mirror the pipeline webhook-auth section. AppAccessConfig owns the
          whole section (header + master toggle + Basic/IP cards) so the master
          switch and the cards share state. No bearer token: a browser
          navigation can't carry an Authorization header. */}
      <AppAccessConfig
        app={app}
        onChanged={() => onRefresh?.({ silent: true })}
        setToast={setToast}
      />

      {/* Dedicated Static IP addon — SOLO apps only. Cloud apps share the
          fleet ALB and have no per-app EIP, so the control is meaningless
          there; gate strictly on deployMode==='solo'. */}
      {app.deployMode === 'solo' && app.appSlug && (
        <StaticIpSection app={app} />
      )}

      {app.deployMode !== 'solo' && (
        <AgentModelModal
          isOpen={agentModalOpen}
          onClose={() => setAgentModalOpen(false)}
          variant="app"
          seedValue={(app.provider || 'claude') === 'codex' ? 'codex' : 'claude'}
          seedKey={app.agentToken || ''}
          onSave={(body) => Apps.setAgent(app.instanceId, body)}
          onSaved={() => onRefresh?.({ silent: true })}
          setToast={setToast}
        />
      )}
    </div>
  );
};

// ─── Usage chart helpers ─────────────────────────────────────────────────
//
// Formatters shared by the Overview "Usage" area charts (card headers +
// MetricChart y-axis ticks/tooltips). `latestOf` pulls the most recent
// sample's value off a `[{t,v}]` series (null when empty) for the header
// "latest value" readouts.

function fmtPct(v) {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  return `${Math.round(v)}%`;
}

// Integer request count (y-axis ticks + header readout). Null/non-finite
// renders an em-dash so empty series degrade gracefully.
function fmtCount(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return `${Math.round(n)}`;
}

// Latency in milliseconds (y-axis ticks + header readout). Null/non-finite
// renders an em-dash so empty series degrade gracefully.
function fmtMs(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return `${Math.round(n)} ms`;
}

function latestOf(series) {
  if (!Array.isArray(series) || series.length === 0) return null;
  const last = series[series.length - 1];
  return typeof last?.v === 'number' ? last.v : null;
}

// ─── Change plan / resource tier section (cloud apps) ────────────────────
//
// Settings-tab card modeled on StaticIpSection: shows the app's current tier
// (vCPU / RAM / ~$/mo), a <select> of the available tiers (filtered to those
// >= the current tier's resources), and a "Change plan" button that opens an
// MktAlertModal confirm, then PATCHes /apps/:id/resources. The resize is a
// brief restart (status → upgrading), so the control is disabled while the
// app isn't running / mid-transition. Tier list comes from the catalog's
// `managedAppTiers`; the current tier comes off the app row.
function ChangePlanSection({ app, instanceId, onRefresh, setToast }) {
  const currentTier = String(app.pricing?.tier || app.tier || 'standard').toLowerCase();
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(currentTier);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load the tier list from the app's catalog entry. Goal/custom apps have
  // no appType (and thus no catalog tiers) — the card still renders the
  // current tier read-only in that case.
  useEffect(() => {
    let cancelled = false;
    if (!app.appType) return undefined;
    (async () => {
      try {
        const data = await Apps.getCatalogEntry(app.appType);
        if (!cancelled && Array.isArray(data?.managedAppTiers)) setTiers(data.managedAppTiers);
      } catch {
        /* non-fatal — leave tiers empty (card stays read-only) */
      }
    })();
    return () => { cancelled = true; };
  }, [app.appType]);

  // Resources the app currently sits on — only offer tiers >= these so we
  // never downsize below the app's minimum footprint.
  const minCpu = app.resources?.cpu ?? 0;
  const minMemory = app.resources?.memory ?? 0;
  const available = tiers.filter((t) => (t.cpu ?? 0) >= minCpu && (t.memory ?? 0) >= minMemory);

  const isRunning = app.status === 'running' || app.status === 'active';

  const tierLabel = (t) => {
    const ram = t.ramGB != null ? `${t.ramGB} GB` : (t.memory ? `${(t.memory / 1024).toFixed(t.memory % 1024 === 0 ? 0 : 1)} GB` : '');
    const vcpu = t.vCpu != null ? `${t.vCpu} vCPU` : (t.cpu ? `${(t.cpu / 1024).toFixed(t.cpu % 1024 === 0 ? 0 : 2)} vCPU` : '');
    const price = t.priceUSD != null ? ` · ~$${t.priceUSD}/mo` : '';
    return `${t.label}${vcpu || ram ? ' · ' : ''}${vcpu}${vcpu && ram ? ' / ' : ''}${ram}${price}`;
  };

  const current = available.find((t) => t.id === currentTier) || tiers.find((t) => t.id === currentTier) || null;

  const apply = async () => {
    if (busy || selectedTier === currentTier) return;
    setBusy(true);
    try {
      await Apps.changeTier(instanceId, selectedTier);
      setToast?.({ isOpen: true, message: 'Plan change started — the app restarts in ~1 min', color: 'success' });
      onRefresh?.({ silent: true });
    } catch (err) {
      setToast?.({ isOpen: true, message: `Plan change failed: ${err.message}`, color: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  const targetTier = available.find((t) => t.id === selectedTier);

  return (
    <div className="page-card app-detail-card" data-testid="change-plan-section">
      <h3 className="pipeline-section-title">Resources / Change plan</h3>
      <p className="pipeline-section-note">
        Current plan: <strong>{current ? tierLabel(current) : currentTier}</strong>.
        Pick a larger plan to give this app more CPU and memory. Changing the
        plan briefly restarts the app (~1 min); your data is preserved.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <select
          className="mkt-field mkt-field--select"
          style={{ flex: '1 1 260px', minWidth: 220 }}
          value={selectedTier}
          onChange={(e) => setSelectedTier(e.target.value)}
          disabled={busy || !isRunning || available.length === 0}
          aria-label="Resource tier"
        >
          {available.length === 0 && <option value={currentTier}>{currentTier}</option>}
          {available.map((t) => (
            <option key={t.id} value={t.id}>{tierLabel(t)}</option>
          ))}
        </select>
        <PillButton
          onClick={() => setConfirmOpen(true)}
          loading={busy}
          disabled={busy || !isRunning || selectedTier === currentTier}
          title={isRunning ? 'Change this app’s plan' : 'App must be running to change plan'}
        >
          Change plan
        </PillButton>
      </div>
      {!isRunning && (
        <p className="apps-modal-helper" style={{ marginTop: 8 }}>
          Start the app to change its plan.
        </p>
      )}

      <MktAlertModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={`Upgrade to ${targetTier ? targetTier.label : selectedTier}?`}
        ariaLabel="Confirm plan change"
        primaryLabel="Yes, change plan"
        onPrimary={() => { setConfirmOpen(false); apply(); }}
        secondaryLabel="Cancel"
      >
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          Moving to{' '}
          <strong style={{ color: '#fff' }}>{targetTier ? tierLabel(targetTier) : selectedTier}</strong>.
          The app <strong style={{ color: '#fff' }}>restarts (~1 min)</strong> while it
          resizes; your data is preserved and billing changes to the new rate.
        </p>
      </MktAlertModal>
    </div>
  );
}

// ─── Dedicated Static IP addon section (solo apps) ───────────────────────
//
// Per-app addon control, modeled on PipelineDetailPage's WarmPoolSection:
// loading → spinner; inactive → "Shared IP" + an enable button (gated by a
// confirm modal + a 402 InsufficientCreditsModal); active → "Dedicated
// static IP" + the current IP + a disable button. Reads the rate from the
// status response (pricing.perMinuteMilliCents) so the price isn't hard-
// coded here — the backend's plans.js / pricing-rates.js are the SSOT.
function StaticIpSection({ app }) {
  const slug = app.appSlug;
  const [status, setStatus] = useState('loading'); // 'loading' | 'inactive' | 'active' | 'error'
  const [staticIp, setStaticIp] = useState(null);
  // Per-minute milli-cent rate — fetched from status so a per-account VIP
  // override stays in sync. Falls back to 12 (the published $5/mo default)
  // only for the moment between mount and first response.
  const [perMinuteRate, setPerMinuteRate] = useState(12);
  const [priceUSD, setPriceUSD] = useState(5);
  const [busy, setBusy] = useState(false);
  const [credsModal, setCredsModal] = useState({ open: false, required: null, balance: null });
  const [confirmEnable, setConfirmEnable] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await Apps.getStaticIpStatus(slug);
      const rate = Number(data?.pricing?.perMinuteMilliCents);
      if (Number.isFinite(rate) && rate > 0) setPerMinuteRate(rate);
      const usd = Number(data?.pricing?.priceUSD);
      if (Number.isFinite(usd) && usd > 0) setPriceUSD(usd);
      if (data?.enabled) {
        setStatus('active');
        setStaticIp(data.staticIp || null);
      } else {
        setStatus('inactive');
        setStaticIp(null);
      }
    } catch (err) {
      console.error('[static-ip] status fetch failed:', err);
      setStatus('error');
    }
  }, [slug]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await Apps.enableStaticIp(slug);
      Credits.fireChangedEvent();
      setStatus('active');
      setStaticIp(res?.staticIp || null);
    } catch (err) {
      if (err?.status === 402) {
        const body = err.body || {};
        setCredsModal({
          open: true,
          required: Number.isFinite(Number(body.requiredMilliCents)) ? Number(body.requiredMilliCents) : null,
          balance: Number.isFinite(Number(body.balanceMilliCents)) ? Number(body.balanceMilliCents) : null,
        });
      } else {
        console.error('[static-ip] enable failed:', err);
        await fetchStatus();
      }
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (busy) return;
    if (typeof window !== 'undefined'
        && !window.confirm('Disable the dedicated static IP? The IP will be released on the next pause and may change on resume. Per-minute charges stop now.')) {
      return;
    }
    setBusy(true);
    try {
      await Apps.disableStaticIp(slug);
      Credits.fireChangedEvent();
      setStatus('inactive');
      setStaticIp(null);
    } catch (err) {
      console.error('[static-ip] disable failed:', err);
      await fetchStatus();
    } finally {
      setBusy(false);
    }
  };

  const perDay = perMinuteRate * 60 * 24;

  return (
    <div className="page-card app-detail-card" data-testid="static-ip-section">
      <div className="card-header"><h2>Dedicated static IP</h2></div>
      <p className="apps-modal-helper">
        By default this app’s public IP is released when it’s paused and a new
        one is assigned on resume. Enable a dedicated static IP to keep the
        same address across pause/resume — so you can safely point an external
        DNS A record at it. Paid add-on: ${priceUSD}/mo ({formatRate(perMinuteRate)}),
        billed per minute while enabled.
      </p>

      {status === 'loading' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <IonSpinner name="dots" />
          <span className="apps-modal-helper" style={{ margin: 0 }}>Loading…</span>
        </div>
      )}

      {status === 'error' && (
        <p className="apps-modal-helper" style={{ color: 'rgba(248, 113, 113, 0.85)' }}>
          Could not load static-IP status.{' '}
          <button type="button" className="warm-pool-link-btn" onClick={fetchStatus}>Retry</button>
        </p>
      )}

      {status === 'inactive' && (
        <>
          <p className="apps-modal-helper" data-testid="static-ip-state">
            Current mode: <strong>Shared IP</strong> (released on pause)
          </p>
          <IonButton
            fill="solid"
            size="default"
            disabled={busy}
            onClick={() => setConfirmEnable(true)}
            data-testid="static-ip-enable"
          >
            <IonIcon slot="start" icon={globeOutline} />
            Enable dedicated static IP ({formatRate(perMinuteRate)})
          </IonButton>
        </>
      )}

      {status === 'active' && (
        <>
          <p className="apps-modal-helper" style={{ margin: '0 0 4px' }} data-testid="static-ip-state">
            Current mode: <strong>Dedicated static IP</strong>
            {staticIp ? <> · <code className="app-detail-mono">{staticIp}</code></> : ''}
          </p>
          <p className="apps-modal-helper" style={{ margin: '0 0 12px' }}>
            Rate: <strong>{formatRate(perMinuteRate)}</strong>
            {' '}· Per-day estimate: <strong>{formatBalance(perDay)}</strong>
          </p>
          <DangerButton icon={trashOutline} onClick={disable} disabled={busy}>
            Disable dedicated static IP
          </DangerButton>
        </>
      )}

      <InsufficientCreditsModal
        open={credsModal.open}
        onClose={() => setCredsModal((s) => ({ ...s, open: false }))}
        title="Add credits to enable a dedicated static IP"
        ariaLabel="Insufficient credits to enable dedicated static IP"
        actionLabel="enable a dedicated static IP"
        rateLabel={formatRate(perMinuteRate)}
        minRuntimeLabel="1 day"
        minRequiredMilliCents={credsModal.required}
        balanceMilliCents={credsModal.balance}
        primaryLabel="Go to Billing"
        primaryTo="/billing"
        secondaryLabel="Cancel"
      />

      <MktAlertModal
        open={confirmEnable}
        onClose={() => setConfirmEnable(false)}
        title="Enable dedicated static IP?"
        ariaLabel="Confirm dedicated static IP enable"
        primaryLabel="Yes, enable"
        onPrimary={() => { setConfirmEnable(false); enable(); }}
        secondaryLabel="Cancel"
      >
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, marginBottom: 12 }}>
          This app’s public IP will be <strong style={{ color: '#fff' }}>retained across pause/resume</strong>,
          so an external DNS A record pointed at it stays valid.
        </p>
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          Per-minute charges begin immediately at{' '}
          <strong style={{ color: '#fff' }}>{formatRate(perMinuteRate)}</strong>
          {' '}(~{formatBalance(perDay)} per day). You can disable any time to stop charges.
        </p>
      </MktAlertModal>
    </div>
  );
}

// ─── Logs tab ──────────────────────────────────────────────────────────
// Polling-based tail of the agent-ops + app container logs via
// GET /apps/:id/logs. Unchanged from v1 — only moved into a tab body.
// Hidden entirely for instances that can't have logs (stub-mode
// services, stopped/destroyed rows).

const LogsTab = ({ app, instanceId }) => {
  // Logs are paginated by CloudWatch `nextToken` (here called `cursor`).
  // First fetch returns the newest page; subsequent "load older" fetches
  // pass the prior cursor and PREPEND older lines onto the existing
  // buffer. Live-tail polling only runs on the newest page (cursor=null
  // AND filter cleared) — once the user starts browsing history we
  // freeze the polling so their scroll position doesn't get stomped.
  const [logs, setLogs] = useState([]);                 // flat array of "<iso>  <message>"
  const [cursor, setCursor] = useState(null);           // next page token (for older lines)
  const [hasMore, setHasMore] = useState(false);        // matches the last response's hasMore
  const [, setLogsLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logsFilter, setLogsFilter] = useState('');       // raw input value
  const [appliedFilter, setAppliedFilter] = useState(''); // debounced, server-side
  // Skeleton gate — true once the first fetch resolves (success or
  // error). Subsequent 3s polls / filter refetches don't flip it back,
  // so the skeleton only flashes on initial mount, not on every
  // refresh or every keystroke in the filter box.
  const [hasLoaded, setHasLoaded] = useState(false);
  const logsBodyRef = useRef(null);
  const stickyBottomRef = useRef(true);
  // Refs mirror the cursor/loading state for callbacks that close over
  // the initial render's values (scroll handler, polling loop).
  const cursorRef = useRef(cursor);
  const hasMoreRef = useRef(hasMore);
  const loadingOlderRef = useRef(loadingOlder);
  useEffect(() => { cursorRef.current = cursor; }, [cursor]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { loadingOlderRef.current = loadingOlder; }, [loadingOlder]);

  // Debounce the user's keystrokes 250ms before kicking off a fresh
  // server-side filter query.
  useEffect(() => {
    const id = setTimeout(() => setAppliedFilter(logsFilter.trim()), 250);
    return () => clearTimeout(id);
  }, [logsFilter]);

  // Live-tail polling — only active when we're on the newest page
  // (cursor=null) so scrolling into history doesn't get clobbered every
  // 3s. Pause/Resume toggle still wins.
  useEffect(() => {
    if (!instanceId || !app) return undefined;
    const serviceArn = app.serviceArn || '';
    const isStub = serviceArn.includes(':stub:');
    // Stub services + destroyed rows have no readable CloudWatch streams.
    // A stopped/failed instance DOES retain history — the per-instance
    // `<instanceId>/agent-ops/` streams survive the task stopping (see
    // getAppLogs) — so we still fetch, just ONCE with no live-tail loop.
    if (isStub || app.status === 'destroyed') return undefined;
    const liveTail = !['stopped', 'failed'].includes(app.status);

    let cancelled = false;
    // Track whether the first fetch has landed — we always run the
    // initial fetchLatest unconditionally so the panel populates, but
    // subsequent interval ticks honour the stickyBottom gate.
    let firstFetchDone = false;
    const fetchLatest = async () => {
      const node = logsBodyRef.current;
      if (node) {
        const slack = 24;
        stickyBottomRef.current = (node.scrollHeight - node.scrollTop - node.clientHeight) <= slack;
      }
      // If the user has scrolled away from the bottom (browsing
      // history, reading mid-buffer, scrolled up to trigger
      // load-older), DON'T replace the buffer — fetchLatest fetches
      // just the latest 200 lines, which would wipe any older pages
      // the user loaded via the top sentinel and cause the viewport
      // to jump as content rewrites underneath them.
      if (firstFetchDone && !stickyBottomRef.current) return;
      setLogsLoading(true);
      try {
        const data = await Apps.getLogs(instanceId, {
          lines: 200,
          filter: appliedFilter || undefined,
        });
        if (cancelled) return;
        const lines = Array.isArray(data?.lines) ? data.lines : [];
        setLogs(lines);
        setCursor(data?.nextToken || null);
        setHasMore(!!data?.hasMore);
        setLogsError(null);
      } catch (err) {
        if (cancelled) return;
        setLogsError(err.message || 'Failed to fetch logs');
      } finally {
        if (!cancelled) {
          setLogsLoading(false);
          setHasLoaded(true);
          firstFetchDone = true;
        }
      }
    };

    fetchLatest();
    // Stopped/failed: one-shot historical fetch, no polling loop. The
    // user can still scroll up (load-older) + filter the retained history.
    if (!liveTail) return () => { cancelled = true; };
    const id = setInterval(fetchLatest, 3000);
    return () => { cancelled = true; clearInterval(id); };
    // appliedFilter triggers a re-fetch from scratch when the user
    // changes the search box (debounced upstream).
  }, [instanceId, app, appliedFilter]);

  // Load-older handler — kicks off when the scroll position is near the
  // top of the buffer (or via the IntersectionObserver sentinel below).
  // Guarded by hasMore + loadingOlder to avoid duplicate fan-out.
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current) return;
    if (!hasMoreRef.current) return;
    if (!cursorRef.current) return;
    setLoadingOlder(true);
    // Remember the current scroll offset so we can restore it after
    // prepending older lines (otherwise the viewport jumps up).
    const node = logsBodyRef.current;
    const prevScrollHeight = node ? node.scrollHeight : 0;
    const prevScrollTop = node ? node.scrollTop : 0;
    try {
      const data = await Apps.getLogs(instanceId, {
        lines: 200,
        cursor: cursorRef.current,
        filter: appliedFilter || undefined,
      });
      const olderLines = Array.isArray(data?.lines) ? data.lines : [];
      setLogs((prev) => [...olderLines, ...prev]);
      setCursor(data?.nextToken || null);
      setHasMore(!!data?.hasMore);
      // After React paints the prepended rows, restore scroll position
      // so the user's viewport stays anchored on the line they were
      // looking at — they only see new content sliding in from the top.
      requestAnimationFrame(() => {
        const n2 = logsBodyRef.current;
        if (!n2) return;
        const delta = n2.scrollHeight - prevScrollHeight;
        n2.scrollTop = prevScrollTop + delta;
      });
    } catch (err) {
      setLogsError(err.message || 'Failed to fetch older logs');
    } finally {
      setLoadingOlder(false);
    }
  }, [instanceId, appliedFilter]);

  // Scroll-position detector: when the user scrolls within 50px of the
  // top of the buffer, request the next older page. Cheap onScroll +
  // ref check (no IntersectionObserver needed for one trigger zone).
  useEffect(() => {
    const node = logsBodyRef.current;
    if (!node) return undefined;
    const onScroll = () => {
      if (node.scrollTop < 50) loadOlder();
    };
    node.addEventListener('scroll', onScroll);
    return () => node.removeEventListener('scroll', onScroll);
  }, [loadOlder]);

  // Auto-scroll to bottom on live-tail updates only — and only when the
  // user is already near the bottom (sticky-tail). If they've scrolled
  // up to browse history, leave them where they are.
  useEffect(() => {
    if (!stickyBottomRef.current) return;
    const node = logsBodyRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [logs]);

  const isStub = String(app.serviceArn || '').includes(':stub:');
  const isDestroyed = app.status === 'destroyed';
  // Stopped/failed instances keep their CloudWatch history — show it
  // (one-shot, no live tail) instead of bailing. Only stub + destroyed
  // (no streams / torn-down log group) get the empty-state card.
  const isHistory = ['stopped', 'failed'].includes(app.status);
  if (isStub || isDestroyed) {
    return (
      <div className="pipeline-tab">
        <div className="page-card app-detail-card">
          <div className="card-header"><h2>Instance log</h2></div>
          <p className="apps-modal-helper">
            {isDestroyed
              ? 'This instance is destroyed — its logs are no longer available.'
              : 'Logs are not available for stub instances.'}
          </p>
        </div>
      </div>
    );
  }

  // Pre-compute the widest line-number column so all numbers align.
  const gutterWidth = Math.max(3, String(logs.length).length);
  // Live-polling indicator: mirror the EXACT conditions the polling
  // useEffect uses to decide whether to keep ticking. `cursor` is set on
  // every successful fetch (CloudWatch's nextToken) so it's almost never
  // null — that's not the right gate. The real gates are: app loaded,
  // not a stub service, status is alive, and user hasn't paused.
  const isPollingLive = !!app
    && !(app.serviceArn || '').includes(':stub:')
    && !['stopped', 'destroyed', 'failed'].includes(app.status);
  return (
    <div className="pipeline-tab">
      <div className="page-card app-detail-card">
        {/* No <h2> title — the tab strip above already reads
            'INSTANCE LOG', a card-level title repeats it. Filter input
            takes the title slot on the left; status + control buttons
            sit on the right. */}
        <div className="card-header app-logs-header">
          <input
            type="text"
            className="app-logs-ctl app-logs-ctl--input"
            placeholder="Filter…"
            value={logsFilter}
            onChange={(e) => setLogsFilter(e.target.value)}
            aria-label="Filter logs"
          />
          <div className="app-logs-controls">
            {isPollingLive ? (
              <span className="app-logs-live" title="Polling every 3s">Live</span>
            ) : isHistory ? (
              <span
                className="app-logs-live app-logs-history"
                title={`Instance is ${app.status} — showing the last recorded logs (not live)`}
              >
                History
              </span>
            ) : null}
          </div>
        </div>

        <pre ref={logsBodyRef} className="app-logs-body">
          {/* Top sentinel — load-older indicator and end-of-history mark. */}
          {hasLoaded && logs.length > 0 && (
            <div className="app-logs-loader">
              {loadingOlder
                ? 'Loading older lines…'
                : (hasMore ? 'Scroll up to load older lines…' : '— End of history —')}
            </div>
          )}
          {!hasLoaded ? (
            // Initial-load skeleton: 8 monospace shimmer rows with a
            // matching gutter so when the real logs paint there's no
            // layout shift. Random-ish widths (66/82/74/…) so it reads
            // as "varied log lines", not three identical bars.
            <div className="app-logs-skeleton" aria-hidden>
              {[66, 82, 74, 90, 58, 78, 86, 64].map((w, i) => (
                <div key={i} className="app-logs-line app-logs-skel-line">
                  <span
                    className="app-logs-lineno app-logs-skel-lineno"
                    style={{ width: '3ch' }}
                  />
                  <span
                    className="app-logs-linetext app-logs-skel-bar"
                    style={{ width: `${w}%` }}
                  />
                </div>
              ))}
            </div>
          ) : logsError ? (
            <span className="app-logs-error">⚠ {logsError}</span>
          ) : logs.length === 0 ? (
            <span className="app-logs-empty">
              {appliedFilter
                ? `No lines match "${appliedFilter}".`
                : (app.status === 'provisioning'
                  ? 'Waiting for agent-ops to emit first log line…'
                  : 'No log events found.')}
            </span>
          ) : (
            logs.map((line, i) => (
              <LogLine key={i} lineNo={i + 1} gutterWidth={gutterWidth} {...parseLogLine(line)} />
            ))
          )}
        </pre>
      </div>
    </div>
  );
};

// ─── ENV tab ───────────────────────────────────────────────────────────
//
// Read-only, suffix-only view of the per-instance env block. Backed by
// GET /apps/:instanceId/env (backend handler `getAppEnv` in apps.js):
//
//   - identity[]    — allow-listed plaintext breadcrumbs (INSTANCE_ID,
//                     ZIBBY_PROJECT_ID, AGENT_OPS_PROVIDER, …).
//   - credentials[] — allow-listed sensitive keys; each row is
//                     { key, present, suffix } with suffix = "…abc123".
//                     Plaintext NEVER crosses the wire.
//
// No edit UI in v1 — to rotate Claude, the user updates their
// workspace-credentials (Settings paste-token card) then runs
// `zibby app update-credential <id>` from the CLI.
const EnvTab = ({ instanceId }) => {
  const [env, setEnv] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Apps.getEnv(instanceId)
      .then((data) => { if (!cancelled) setEnv(data || null); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load env'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [instanceId]);

  // Mirror the workflow ENV tab atom-by-atom (see PipelineDetailPage.js
  // around line 1592–1624): `.pipeline-tab` shell + `.pipeline-env-header`
  // hint row + `<DataTable>` for the rows. No inline styles — the table
  // chrome and the env-cell typography (`.pipeline-env-key`,
  // `.pipeline-env-mask*`) all come from the shared pipelines stylesheet
  // we already import at the top of this file.

  const credentials = env?.credentials || [];
  const identity = env?.identity || [];

  const credentialColumns = [
    {
      header: 'KEY',
      key: 'key',
      render: (r) => <code className="pipeline-env-key">{r.key}</code>,
    },
    {
      header: 'SUFFIX',
      key: 'suffix',
      render: (r) => (
        r.present
          ? (
            <span className="pipeline-env-mask">
              <span className="pipeline-env-mask-dots" aria-hidden="true">{'•'.repeat(24)}</span>
              <span className="pipeline-env-mask-suffix">{r.suffix}</span>
            </span>
          )
          : <span className="pipeline-env-mask"><em style={{ opacity: 0.5 }}>not set</em></span>
      ),
    },
  ];

  const identityColumns = [
    {
      header: 'KEY',
      key: 'key',
      render: (r) => <code className="pipeline-env-key">{r.key}</code>,
    },
    {
      header: 'VALUE',
      key: 'value',
      render: (r) => <span className="pipeline-env-mask">{r.value || '—'}</span>,
    },
  ];

  return (
    <div className="pipeline-tab">
      <div className="pipeline-env-header">
        <p className="pipeline-section-hint">
          Sensitive tokens — values are masked to the last 6 characters so you
          can confirm the right credential is wired up without exposing the
          plaintext. To rotate: paste a new token in{' '}
          <a href="/integrations">Settings → Integrations</a>, then run{' '}
          <code>zibby app update-credential {instanceId}</code> from the CLI.
        </p>
      </div>
      <DataTable
        data={credentials}
        columns={credentialColumns}
        loading={loading}
        rowKey="key"
        emptyState={{
          title: error || 'No credentials',
          description: error
            ? 'Could not load the instance env. Try refreshing.'
            : 'No credential env vars present on this instance yet — likely still provisioning.',
        }}
      />

      <div className="pipeline-env-header" style={{ marginTop: 24 }}>
        <p className="pipeline-section-hint">
          Non-sensitive env vars the agent uses to identify itself + route
          outbound calls. Plaintext is safe to display.
        </p>
      </div>
      <DataTable
        data={identity}
        columns={identityColumns}
        loading={loading}
        rowKey="key"
        emptyState={{
          title: 'No identity vars',
          description: 'Identity env is populated once the task is running.',
        }}
      />
    </div>
  );
};

// ─── Agent activity tab ────────────────────────────────────────────────
// Reads structured run records from the control plane
// (GET /apps/:id/runs → the `zibby-{stage}-app-runs` DynamoDB table the
// agent-ops daemon pushes to after every finished run). This replaced
// the old approach of grepping CloudWatch for `claudecli: conversation
// complete` lines, which silently dropped failed runs (they emit no
// such line) and only ever surfaced the earliest few records before the
// log budget ran out. See AGENT_RUNS_PLAN.md for the full contract.

/**
 * ConversationsTab — agent run history viewer.
 *
 * List (lightweight): GET /apps/:id/runs?limit=50, newest first. Polled
 * every 10s (runs are ~hourly, so no need for a 3s tail cadence). Each
 * row shows local time · headline (taskName / summary) · a status badge
 * · turns · cost — including FAILED/TIMEOUT runs, which the log-grep
 * approach couldn't see.
 *
 * Detail (heavy): expanding a row lazy-fetches GET /apps/:id/runs/:runId
 * (the list omits systemPrompt / userPrompt / result to stay cheap) and
 * caches it per runId so re-expanding doesn't refetch.
 */

// status → badge class + label. completed=green, failed=red,
// timeout/canceled=amber, running=muted/pulse. Reuses the existing log
// colour tokens via the `.app-convo-badge--*` classes in the stylesheet
// (no new palette).
const RUN_STATUS_BADGE = {
  completed: { cls: 'app-convo-badge--ok',     label: 'completed' },
  failed:    { cls: 'app-convo-badge--err',    label: 'failed' },
  timeout:   { cls: 'app-convo-badge--warn',   label: 'timeout' },
  canceled:  { cls: 'app-convo-badge--warn',   label: 'canceled' },
  running:   { cls: 'app-convo-badge--running', label: 'running' },
};

const ConversationsTab = ({ instanceId, appStatus }) => {
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  // Per-runId detail cache: { [runId]: { loading, error, data } }. Keeps
  // an expanded row from refetching on re-expand and survives list polls.
  const [details, setDetails] = useState({});
  // hasLoaded gates the skeleton — true once the first fetch resolves
  // (success OR error). Subsequent 10s polls don't flip it, so the
  // skeleton only flashes on the initial mount, not on every refresh.
  const [hasLoaded, setHasLoaded] = useState(false);
  // Infinite scroll over older runs. Runs are newest-FIRST: the live poll
  // refreshes the top (newest) page; scrolling to the bottom appends OLDER
  // pages via the DDB cursor (`nextToken`). `cursor`/`hasMore` track the
  // OLDEST boundary loaded so far and are advanced only by loadOlder + the
  // first poll — never overwritten by later polls (which fetch the newest
  // page and would otherwise reset pagination).
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const PAGE = 50;
  const sentinelRef = useRef(null);
  // Refs mirror state for the IntersectionObserver / poll callbacks that
  // close over a stale render.
  const cursorRef = useRef(cursor);
  const hasMoreRef = useRef(hasMore);
  const loadingOlderRef = useRef(loadingOlder);
  const initializedRef = useRef(false);
  useEffect(() => { cursorRef.current = cursor; }, [cursor]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { loadingOlderRef.current = loadingOlder; }, [loadingOlder]);

  // Reset pagination when the instance changes (switching apps).
  useEffect(() => {
    initializedRef.current = false;
    setRuns([]); setCursor(null); setHasMore(false);
    setHasLoaded(false); setError(null);
  }, [instanceId]);

  // Merge a freshly-polled newest page into the accumulated list,
  // newest-first, de-duped by runId. The newest page wins for any run it
  // also contains (fresher status/cost), and already-loaded OLDER pages
  // are preserved below it. (Runs are ~hourly, so a new run shifting the
  // page boundary at most duplicates one row across a fetch — dedupe
  // handles it; a missed in-between run is acceptable at this cadence.)
  const mergeNewest = (newest, prev) => {
    const ids = new Set(newest.map((r) => r.runId));
    return [...newest, ...prev.filter((r) => !ids.has(r.runId))];
  };

  // 10s polling — no pause toggle (agent runs land at the top hourly,
  // never clobbering an expanded row below). Skip when the instance is
  // stopped/destroyed. Newest-first ordering comes from the backend.
  useEffect(() => {
    if (!instanceId) return undefined;
    // Agent run records live in the app-runs table and PERSIST after the
    // app stops — a stopped/failed app still has history to show. Fetch it
    // ONCE (no live poll) instead of bailing; bailing left hasLoaded false
    // forever, so the skeleton spun indefinitely on a stopped app.
    const isFinal = ['stopped', 'destroyed', 'failed'].includes(appStatus);

    let cancelled = false;
    const fetchRuns = async () => {
      try {
        const data = await Apps.listRuns(instanceId, { limit: PAGE });
        if (cancelled) return;
        const fresh = Array.isArray(data?.runs) ? data.runs : [];
        if (!initializedRef.current) {
          // First successful load — seed the list AND the older-boundary
          // cursor from page 1.
          setRuns(fresh);
          setCursor(data?.nextToken || null);
          setHasMore(!!data?.hasMore);
          initializedRef.current = true;
        } else {
          // Subsequent polls only refresh the newest page; leave the
          // older-boundary cursor/hasMore untouched so loaded history
          // stays put.
          setRuns((prev) => mergeNewest(fresh, prev));
        }
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch runs');
      } finally {
        if (!cancelled) setHasLoaded(true);
      }
    };

    fetchRuns();
    // Stopped/failed: one-shot historical fetch, no polling loop.
    if (isFinal) return () => { cancelled = true; };
    const id = setInterval(fetchRuns, 10_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [instanceId, appStatus]);

  // Load the next OLDER page, appended to the bottom. Guarded by
  // hasMore + in-flight so the observer can't double-fire.
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current || !hasMoreRef.current || !cursorRef.current) return;
    setLoadingOlder(true);
    try {
      const data = await Apps.listRuns(instanceId, { limit: PAGE, cursor: cursorRef.current });
      const older = Array.isArray(data?.runs) ? data.runs : [];
      setRuns((prev) => {
        const ids = new Set(prev.map((r) => r.runId));
        return [...prev, ...older.filter((r) => !ids.has(r.runId))];
      });
      setCursor(data?.nextToken || null);
      setHasMore(!!data?.hasMore);
    } catch (err) {
      setError(err.message || 'Failed to load older runs');
    } finally {
      setLoadingOlder(false);
    }
  }, [instanceId]);

  // Infinite-scroll trigger: fire loadOlder when the bottom sentinel
  // scrolls into view. Re-attaches when the list (and thus the sentinel)
  // first renders.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadOlder();
    }, { rootMargin: '200px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [loadOlder, hasLoaded, runs.length]);

  const toggle = (runId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
        return next;
      }
      next.add(runId);
      // Lazy-fetch the full record on first expand only — cache per
      // runId so collapse/re-expand never refetches.
      setDetails((d) => {
        if (d[runId]) return d; // already fetched (or in-flight)
        Apps.getRun(instanceId, runId)
          .then((data) => setDetails((dd) => ({ ...dd, [runId]: { loading: false, error: null, data } })))
          .catch((err) => setDetails((dd) => ({ ...dd, [runId]: { loading: false, error: err.message || 'Failed to load run', data: null } })));
        return { ...d, [runId]: { loading: true, error: null, data: null } };
      });
      return next;
    });
  };

  return (
    <div className="pipeline-tab">
      <div className="page-card app-detail-card">
        <div className="card-header app-logs-header">
          <h2>Agent activity</h2>
          <div className="app-logs-controls">
            {/* No pause/stop toggle here — new runs arrive ~hourly and
                land at the top of the list without clobbering anything
                expanded below, so the pause button has nothing to do.
                Just the polling indicator — muted "History" when stopped. */}
            {['stopped', 'destroyed', 'failed'].includes(appStatus) ? (
              <span
                className="app-logs-live app-logs-history"
                title="Instance stopped — showing recorded run history (not live)"
              >
                History
              </span>
            ) : (
              <span className="app-logs-live" title="Polling every 10s">Live</span>
            )}
          </div>
        </div>

        {!hasLoaded ? (
          // Skeleton: 3 ghost rows shimmering while the first fetch is
          // in-flight. We don't know the real count yet — 3 is enough to
          // read as "list", small enough to avoid pretending we have
          // data. Once `hasLoaded` flips (success or error), this is
          // replaced by the real list / empty / error.
          <ul className="app-convo-list app-convo-skeleton" aria-hidden>
            {[0, 1, 2].map((i) => (
              <li key={i} className="app-convo-item app-convo-skel-item">
                <div className="app-convo-summary app-convo-skel-summary">
                  <div className="app-convo-time app-convo-skel-bar app-convo-skel-bar--time" />
                  <div className="app-convo-headline app-convo-skel-bar app-convo-skel-bar--headline" />
                  <div className="app-convo-meta app-convo-skel-bar app-convo-skel-bar--meta" />
                </div>
              </li>
            ))}
          </ul>
        ) : error ? (
          <p className="app-logs-error">⚠ {error}</p>
        ) : runs.length === 0 ? (
          <p className="apps-modal-helper">
            No agent runs recorded yet. Runs from agent-ops v0.1.22+ appear here.
          </p>
        ) : (
          <ul className="app-convo-list">
            {runs.map((r) => {
              const isOpen = expanded.has(r.runId);
              const badge = RUN_STATUS_BADGE[r.status] || { cls: 'app-convo-badge--muted', label: r.status || 'unknown' };
              const headline = r.taskName || firstLine(r.summary) || '(unnamed run)';
              const cost = formatRunCost(r.costUsdMicro);
              const detail = details[r.runId];
              return (
                <li key={r.runId} className={`app-convo-item ${isOpen ? 'is-open' : ''}`}>
                  <button type="button" className="app-convo-summary" onClick={() => toggle(r.runId)}>
                    <div className="app-convo-time" title={r.startedAt ? timeAgo(r.startedAt) : ''}>
                      {formatLocalTime(r.startedAt)}
                    </div>
                    <div className="app-convo-headline">{headline}</div>
                    <div className="app-convo-meta">
                      <span className={`app-convo-badge ${badge.cls}`}>{badge.label}</span>
                      <span>{r.numTurns ?? '?'} turn{r.numTurns === 1 ? '' : 's'}</span>
                      <span>{cost}</span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="app-convo-body">
                      {detail?.loading && (
                        <div className="app-convo-section-hint">Loading run detail…</div>
                      )}
                      {detail?.error && (
                        <p className="app-logs-error">⚠ {detail.error}</p>
                      )}
                      {detail?.data && (
                        <>
                          {detail.data.systemPrompt && (
                            <div className="app-convo-section">
                              <div className="app-convo-section-label">Agent context</div>
                              {/* Plain-English clarifier: what users see is NOT
                                  the full system prompt the model received.
                                  Claude Code CLI auto-injects its own preamble
                                  (tool schemas, output format, safety rules)
                                  that's hardcoded in the `claude` binary and
                                  not reachable from us. This block is only
                                  the slice agent-ops prepends on top. */}
                              <div className="app-convo-section-hint">
                                What agent-ops prepended for this run. Claude Code CLI's built-in
                                preamble (tool schemas, output format, safety) is invisible to us.
                              </div>
                              <pre className="app-convo-text">{detail.data.systemPrompt}</pre>
                            </div>
                          )}
                          {detail.data.userPrompt && (
                            <div className="app-convo-section">
                              <div className="app-convo-section-label">User prompt</div>
                              <pre className="app-convo-text">{detail.data.userPrompt}</pre>
                            </div>
                          )}
                          {detail.data.result && (
                            <div className="app-convo-section">
                              <div className="app-convo-section-label">Claude's response</div>
                              <pre className="app-convo-text app-convo-result">{detail.data.result}</pre>
                            </div>
                          )}
                          {r.status !== 'completed' && (detail.data.error || r.error) && (
                            <div className="app-convo-section">
                              <div className="app-convo-section-label">Error</div>
                              <pre className="app-convo-text app-convo-error">{detail.data.error || r.error}</pre>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
            {/* Bottom sentinel — the IntersectionObserver watches this to
                auto-load the next older page as it scrolls into view. The
                footer doubles as the loading / end-of-history marker. */}
            <li ref={sentinelRef} className="app-convo-loader" aria-hidden>
              {loadingOlder
                ? 'Loading older runs…'
                : (hasMore ? '' : '— End of history —')}
            </li>
          </ul>
        )}
      </div>
    </div>
  );
};

// First non-empty line of a multi-line string, used as a headline
// fallback when a run has no taskName.
function firstLine(s) {
  if (!s) return '';
  const t = String(s).split('\n')[0].trim();
  return t.length > 120 ? t.slice(0, 117) + '…' : t;
}

// Format a run's cost from microdollars (costUsdMicro / 1e6). Sub-cent
// values get 4 decimals ($0.0042); cent-and-up get 2 ($1.23). Mirrors
// the old `total_cost_usd` rendering.
function formatRunCost(costUsdMicro) {
  if (costUsdMicro == null || !Number.isFinite(Number(costUsdMicro))) return '—';
  const usd = Number(costUsdMicro) / 1e6;
  return `$${usd < 0.01 ? usd.toFixed(4) : usd.toFixed(2)}`;
}

function formatLocalTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────

function formatBytes(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const v = n / Math.pow(1024, i);
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function safeCronstrue(expr) {
  try { return cronstrue.toString(expr, { use24HourTimeFormat: true }); }
  catch { return expr; }
}

export default AppDetailPage;

// Exposed for unit tests (mirrors PipelineDetailPage's
// __test_only_WarmPoolSection). The section is module-private; this lets a
// test mount + exercise its state machine without the full router/outlet
// context the page wrapper needs.
export { StaticIpSection as __test_only_StaticIpSection };
